// The cloud as the server's windows ("cloud_win" messages, simulation/src/ringvol.py), drawn by ray marching.
//
// The server says where the cloud is and how much light reaches each part of it; this file draws that and nothing else. The cloud is in nested windows round the observer, a
// clipmap: four rings, each a window of 64 x 64 columns (125 m cells over 8 km, 250 m over 16 km, 500 m over 32 km, 1 km over 64 km), and the world level, 2 km cells all round the (periodic)
// world. Each is the same cloud at its own resolution: a coarser level is the mean of the finer one over its cells, and the invention below the truth is the same function of place and
// time at every level. Which level a point is read from depends on its distance from the eye (3D: up and down are not special): the finest ring that holds it and is near enough, blended
// into the next coarser one over the outer quarter of its reach, so there is no place where the picture changes resolution.
// Per sample: one fetch of the density (a byte of sqrt(extinction)) and one of the baked light (sun beam transmittance, sky openness, roughness; pooled 2 x 2 x 2, 7 bits). Per pixel: one
// cloudColor() call on the opacity weighted means, which is the dome's own lighting (Sky.cellUniforms: the lit table by view direction, sun and sky
// colour, white balance, scotopic shift, tone curve, haze toward the sky colour), so the volume sits in the same sky as the dome's layers.
//
// The march skips empty space with the server's per block height extents, and takes 0.6 cell steps inside cloud. The cloud is drawn as the solver made it, with one addition: where a cell
// is several pixels wide (cloud near the camera) its edge is eroded by a self-similar cascade of billowy noise (setDetail), which keeps each cell's mean extinction, moves with the cloud
// (the noise's frame is carried by the cloud wind) and fades out when the biggest lobe its level's cells can carry shrinks to a few pixels, so the model's water is what is shown and the structure below its resolution is presentation. How much of
// it each cloud gets is the server's: its roughness field (the light grid's blue channel) says from the weather how soft or how crisp that cloud is, so a stratus slab stays smooth and a
// cumulus is cut. Optionally (setQuality):
//   * lobe shading: taps toward the light at three growing distances (in pixels of footprint) compare the density there with the density here, so each lobe of the model's own cloud gets a lit
//     and a shaded side at every range. It reads the volume only.
// A window is the cloud in the ground's frame at the model time it was painted; the server sends a ring again every few model seconds (the finer, the oftener), and between two messages the
// client cross-fades the old state into the new one (a cloud moves less than a cell in that time, so the fade is the motion). A window that has moved (the eye left the middle of it) is
// shifted by whole columns first, so that the fade is of the same cloud at the same place.
import * as THREE from 'three';
import type { Sky } from './sky';
import { NOISE_GLSL, SKY_PROBE_LAYER, SKY_VIEW_GLSL } from './sky';
import { CLOUD_BEAM_GLSL } from './atmosphereGpu';
import { AMBIENT_SH_GLSL } from './ambientSh';
import { SCOTOPIC, SCOTOPIC_ROD_W, SCOTOPIC_TINT_N } from './exposure';
import type { RingLayer, VolView } from './skyLight';
import { air, AIR_GLSL, AIR_MAP_GLSL } from './air';
import { origin } from './origin';
import { planet, visibleDistance } from './planet';
import { COARSE_BEND, COARSE_WARP, DET_COARSE, DET_L0, DET_LC, DET_N, DET_OCT, DET_P0, DET_PC, WORLD_BOOST, makeDetailNoise } from './cloudDetail';
import { HANDOVER_GLSL, handover } from './handover';
import { GROUND_GLSL, type GroundClip } from './groundClip';
import { RINGS as GROUND_RINGS } from './groundWire';

/** The levels: 0..3 the rings from the finest, 4 the world. The server's table (ringvol.LEVELS_TABLE) has the same: the shapes are fixed, and a message that disagrees is refused. */
export const N_RINGS = 4, N_LEVELS = 5, WORLD_LEVEL = 4;
const RING_N = 64, RING_NZ = [320, 160, 80, 40], WORLD_N = 256, WORLD_NZ = 20, EXT_BLK = 4;
const RING_NB = RING_N / EXT_BLK, WORLD_NB = WORLD_N / EXT_BLK;
const RAIN_N = RING_N / 2, RAIN_NW = WORLD_N / 2;                 // the rain planes: a texel is 2 x 2 columns
const VOFF: number[] = [], LOFF: number[] = [];                 // each ring's first layer in the stacked density and light textures
let VTOT = 0, LTOT = 0;
for (let k = 0; k < N_RINGS; k++) { VOFF.push(VTOT); LOFF.push(LTOT); VTOT += RING_NZ[k]!; LTOT += RING_NZ[k]! / 2; }
const NOISE_WRAP = 12800;                                       // every period of the detail noise divides this (see the shader)

export interface CloudWinMessage {
  type: 'cloud_win'; level: number; t: number; tick: number; rate: number; ox: number; oy: number; world_m: number;
  /** n columns across, nz layers; dx, dz the cell and the layer (m); z0 the height of the bottom; x0, z0m the window's north-west corner (client axes: x east, z south); ext_max the extinction at byte 255 */
  vol: { n: number; nz: number; dx: number; dz: number; z0: number; pool: number; x0: number; z0m: number; ext_max: number };
  lit: { n: number; nz: number; levels: number };
  rough?: { n: number; nz: number; part: string };
  ext: { nb: number; blk: number; zmax: number; bands?: number; split?: number; d: string };
  /** The rain under the cloud (ringvol.rain_planes): n x n texels of 2 x 2 columns, two planes (zlib, base64): the rate (sqrt scale over rmax mm/h) and the first layer of the cloud above (a layer is dz m). Absent from an older server: dry. */
  rain?: { n: number; rmax: number; dz: number; d: string };
  drift: [number, number];                         // the cloud's velocity over the ground (m/s, x east, z south)
  cover?: number;
  stats?: Record<string, unknown>;
  /** Set by the client (cloudVolumeWire.ts) from the binary parts: the density, light and roughness, inflated and unfiltered. `decUsed`: ingest has taken them
   *  (the scene may ingest the same message again after a rebuild; it then works on copies, because the first ingest keeps and recycles its own arrays). */
  dec: { vol: Uint8Array; lit: Uint8Array; win: Uint8Array };
  decUsed?: boolean;
}

const SHARED_GLSL = /* glsl */`
precision highp float; precision highp sampler3D; precision highp sampler2D;
uniform vec3 uSunDir;
uniform vec3 uSrc; uniform vec3 uAmb; uniform float uGain; uniform vec2 uCgain; uniform float uNight;
${AMBIENT_SH_GLSL}
uniform float uHpSky; uniform float uHpCloud; uniform vec3 uLDir; uniform float uLUp; uniform float uWB;
uniform vec3 uMoonSrc; uniform float uSrgb; uniform float uFrame;
const float PI = 3.14159265;
${SKY_VIEW_GLSL}
${CLOUD_BEAM_GLSL}
const vec3 ROD  = vec3(${SCOTOPIC_ROD_W.map(v => v.toFixed(5)).join(', ')});
const vec3 TINT = vec3(${SCOTOPIC_TINT_N.map(v => v.toFixed(5)).join(', ')});
const float SCOT = ${SCOTOPIC.STRENGTH.toFixed(3)};
const float NIGHT_AMB = 1.8;
${NOISE_GLSL}
${AIR_GLSL}${AIR_MAP_GLSL}
uniform vec3 uCamRel;   // the eye relative to the floating origin O (origin.ts), set for each camera that draws; every position in this shader is counted from O
float luma(vec3 c) { return dot(c, vec3(0.2126, 0.7152, 0.0722)); }
vec3 whiten(vec3 c) { return mix(c, vec3(luma(c)), uWB); }
vec3 scoto(vec3 c) { float rod = dot(c, ROD); return c + (rod * TINT - c) * (SCOT * uNight); }
vec3 srgbToLinear(vec3 c) { return mix(c / 12.92, pow((c + 0.055) / 1.055, vec3(2.4)), step(vec3(0.04045), c)); }
vec3 tone(vec3 x, float hp) {
  vec3 a = 1.0 - exp(-x);
  float m = max(max(x.r, x.g), max(x.b, 1e-6));
  return mix(a, x / m * (1.0 - exp(-m)), hp);
}
vec3 skyTone(vec3 d) { return tone(scoto(skyLinear(d)) * uGain, uHpSky); }
vec3 cloudColor(float beamT, float ambF, float shade, vec3 d, float dist, vec3 ambR) {
  vec3 lit = whiten(uSrc * cloudBeam(d, dist)) + uMoonSrc;
  float fwd = 1.0 + 0.9 * pow(max(dot(d, uLDir), 0.0), 6.0);
  vec3 litF = mix(lit, vec3(luma(lit)), 0.5);
  vec3 direct = fwd * (lit * (0.95 * beamT) + litF * 0.14);
  vec3 amb = uAmb * ambR * ambF * (1.0 + NIGHT_AMB * uNight);
  vec3 cdt = tone(scoto(direct) * uCgain.x * shade + scoto(amb) * uGain * (0.7 + 0.3 * shade), uHpCloud);
  vec3 airT = airTransmitMap(uCamRel, d, dist);                                       // the air between the eye and the cloud (air.ts): what it takes out of the cloud's colour it puts back as the sky's
  cdt = cdt * airT + skyTone(d) * (1.0 - airT);
  return mix(cdt, srgbToLinear(cdt), uSrgb * (1.0 - smoothstep(0.35, 1.0, uNight)));
}
`;

/** The distance a pixel's cloud is stored at in the depth attachment of the march target (and read back by the composite and the temporal resolve): s = t / (t + DEPTH_K) for a hit
 *  at t metres along the ray, 1 for no cloud. It does not depend on the camera's near and far, which the renderer moves with the eye, so a picture stays valid when they change. */
export const DEPTH_K = 1000;

/** How many depth slices the composite draws a pixel's cloud as (see COMP_FRAG). */
export const COMP_LAYERS = 4;
/** The two distances a pixel's cloud is stored at (the march's depth attachment, a float32; read by the temporal resolve and the composite): where its cloud starts (tf) and where it ends (tb), both as
 *  d = t / (t + DEPTH_K), packed into one number: the start in the top 11 bits (a multiple of 1 / 2048), the share of the way from there to 1 that the end reaches in the 11 below (every product here is a
 *  power of two, so the float holds all 22 bits exactly). No cloud is 1. The packed numbers order like their start, so the nearest of several is the one whose cloud starts first. */
const DEPTH_PACK_GLSL = /* glsl */`
float packDepth(float tf, float tb) {
  float dF = tf / (tf + DEPTH_K), dB = tb / (tb + DEPTH_K);
  float qF = floor(dF * 2048.0) / 2048.0;
  float s = clamp((dB - qF) / max(1.0 - qF, 1e-6), 0.0, 0.9995);
  return qF + floor(s * 2048.0) / 4194304.0;
}
void unpackDepth(float p, out float tf, out float tb) {
  float q = p * 2048.0;
  float qF = floor(q) / 2048.0;
  float dB = qF + fract(q) * (1.0 - qF);
  tf = DEPTH_K * qF / (1.0 - qF);
  tb = DEPTH_K * dB / (1.0 - dB);
}`;

const VERT = /* glsl */`
varying vec3 vDir;
varying vec4 vPr;                                                                             // the projection's terms the fragment needs for the depth it writes: [0][0], [1][1], [2][2], [3][2]
uniform vec2 uJ;                                                                              // a sub-pixel shift of the ray through each pixel, in clip units (the temporal march, see CloudVolume.renderView; 0 otherwise)
void main() {
  vec3 vd = vec3((position.x + uJ.x) / projectionMatrix[0][0], (position.y + uJ.y) / projectionMatrix[1][1], -1.0);
  vDir = vd * mat3(viewMatrix);
  vPr = vec4(projectionMatrix[0][0], projectionMatrix[1][1], projectionMatrix[2][2], projectionMatrix[3][2]);
  gl_Position = vec4(position.xy, 0.9999, 1.0);
}`;

/** The levels' uniforms and lookups, shared by the march and by the cloud shadow pass (cloudShadow.ts): which level a point is read from (ringSet) and the density byte there (readD). */
export const LEVEL_UNIFORMS_GLSL = /* glsl */`
uniform sampler3D uVolR, uLitR, uVolW, uLitW;                                                 // the rings' density and light (the four rings stacked along the layers), and the world level's own
uniform vec4 uRg[5];                                                                          // per level (0 the finest ring .. 3 the coarsest, 4 the world): the window's corner x, z (m, north-west), its edge (m), 1 once it is loaded
uniform vec4 uRgZ[5];                                                                         // per level: its first layer in the density texture, its layers, its first layer in the light texture, the light's layers
uniform vec4 uRgM[5];                                                                         // per level: how far it is used (m), the width of its edge fade (m), blocks across, the row of its block table in uExtR
uniform float uDx[5];                                                                         // per level: the cell (m)
uniform vec4 uZt;                                                                             // layers in uVolR, in uLitR, in uVolW, in uLitW
uniform float uWorldM, uSpan, uExtMax, uAny;                                                  // the world's period (m), the height the layers cover (m), the extinction at byte 255 (1/m), 1 once any level is loaded
`;
export const LEVEL_FUNCS_GLSL = /* glsl */`
// ---- the levels ---------------------------------------------------------------------------------------------------------------------------
// The cloud is in nested windows centred (give or take a quarter of a window) on the eye: four rings, each a window of 64 columns, 125 m, 250 m, 500 m and 1 km wide, and the world level, 2 km
// columns all round the world (which is periodic: the coordinates are taken modulo the world's edge). A point is read from the finest ring that holds it and is near enough: a ring counts in
// full up to three quarters of its reach and fades out to the edge of it (and fades in over a few cells at the edge of its window, where the window might not hold the point), and what it leaves
// goes to the next coarser level, so the picture is a smooth blend of two or three levels at most. Which level a point is read from depends on its distance from the eye, in 3D: up or down are the same.
// Every level has the same height range and the same unit (the byte is the square root of the extinction over uExtMax), and a coarser level holds the mean of the finer one over its cells.
float ringA(int k, vec3 pw, float dist) {
  vec4 g = uRg[k];
  if (g.w < 0.5) return 0.0;
  if (k == 4) return 1.0;
  vec2 dd = mod(pw.xz - g.xy, uWorldM);
  vec2 e = min(dd, g.z - dd);                                                                 // the distance to the window's nearest edge (negative outside)
  float cov = clamp(min(e.x, e.y) / uRgM[k].y, 0.0, 1.0);
  float R = uRgM[k].x;
  return cov * (1.0 - smoothstep(0.75 * R, R, dist));
}
// The levels that count at a point, finest first (at most three), and their weights, which add up to 1 (the last takes what the others leave). Returns how many.
int ringSet(vec3 pw, float dist, out int k0, out int k1, out int k2, out vec3 ws) {
  int n = 0; float rem = 1.0;
  k0 = 4; k1 = 4; k2 = 4; ws = vec3(0.0);
  for (int k = 0; k < 5; k++) {
    float a = ringA(k, pw, dist);
    if (a <= 0.0) continue;
    float w = rem * a;
    if (n == 0) { k0 = k; ws.x = w; } else if (n == 1) { k1 = k; ws.y = w; } else { k2 = k; ws.z = w; }
    n++; rem -= w;
    if (n == 3) { ws.z += rem; break; }
    if (rem < 0.003) break;
  }
  return n;
}
vec2 levelUV(int k, vec3 pw) { return mod(pw.xz - uRg[k].xy, uWorldM) / uRg[k].z; }
// The density byte (the root of the extinction over uExtMax) of level k at a point: 0 above and below its layers.
float readD(int k, vec3 pw) {
  vec4 z = uRgZ[k];
  float f = pw.y / uSpan * z.y;
  if (f < 0.0 || f > z.y) return 0.0;
  float fc = clamp(f, 0.5, z.y - 0.5);                                                        // (never blends with the next ring's layers in the stack)
  vec2 uv = levelUV(k, pw);
  if (k == 4) return textureLod(uVolW, vec3(uv, (z.x + fc) / uZt.z), 0.0).r;
  return textureLod(uVolR, vec3(uv, (z.x + fc) / uZt.x), 0.0).r;
}
`;

const FRAG = SHARED_GLSL + /* glsl */`
${LEVEL_UNIFORMS_GLSL}
uniform sampler2D uExtR, uExtW;                                                               // the height ranges of their blocks (the rings' tables stacked by rows)
uniform sampler2D uRainR, uRainW;                                                             // the rain under the cloud: R the rate (root scale), G the first layer of the cloud above it; 2 x 2 columns a texel, linear
uniform vec3 uRainU;                                                                          // the rate at byte 255 (mm/h), a layer's height (m), the strength of the rain's extinction (1: as measured)
uniform vec4 uFar;                                                                            // (x) steps grow with range by this share of it, fade start (share of uMaxD), air scale (1: the air as the world has it, air.ts)
uniform float uMaxD, uMaxA, uDebug, uFine;                                                    // uMaxD: how far the low cloud (below about 3 km) is followed, uMaxA: how far the layers aloft are (cirrus is seen to the horizon); both follow the eye (see horizonFor); uDebug: 1 shows the work per pixel (reads, over 256), 2 the same over 1024, 3 the march's loop passes over 220
uniform float uStepMax;                                                                       // the longest step inside cloud (m; 1e9 for none)
uniform float uShade;                                                                         // lobe shading strength (setShade)
uniform float uTaps;                                                                          // how many taps the multi-scale shading makes (0 to 3; the quality tier)
${GROUND_GLSL}
uniform float uGndMax;                                                                        // the highest ground in the ground clipmap (m above the sea); under 0.5 the march ignores the ground (no clipmap yet, a flat sea, or ?groundclip=off)
// How far p (counted from O, like every position here) is above the ground (m, negative inside the rock): the height of the finest ground ring that draws p, and 1e9 where no ring draws it (the
// clipmap is not extended past its edge: beyond it nothing is known) or p is above all the ground.
float aboveGround(vec3 p) {
  if (p.y >= uGndMax) return 1e9;
  for (int k = uGKmin; k < ${GROUND_RINGS}; k++) {
    vec4 w = uGWin[k];
    vec2 u = vec2((p.x - w.x) * w.z, (w.y - p.z) * w.z);
    vec4 o = uGO[k];
    if (u.x >= o.x && u.y >= o.y && u.x <= o.z && u.y <= o.w) return p.y - max(groundBilinear(k, u), 0.0);
  }
  return 1e9;
}
uniform float uGrowMax;                                                                       // how far the steps grow in clear air inside a block's range (6; 1 for none)
uniform vec4 uFx2;                                                                            // first tap of the multi-scale shading (px of footprint), the taps' ratio, nearest range where it acts (m), range where it is full (m)
uniform float uSteep;                                                                          // exponent on the density share (1: the extinction as the solver made it)
uniform float uJit;                                                                           // how much the step position is jittered per pixel (1; 0 for a fixed offset)
uniform float uPx;                                                                            // the size of a pixel of this draw against the one the footprint constants (0.0016, 0.0022) were tuned for: 1 at full size, 2 when the volume is drawn at half size
uniform float uMaxSteps;                                                                      // the march's step budget (220; the loop's own limit is fixed, this ends it early)
uniform vec2 uClip;                                                                           // draw only cloud between these heights (m): to look at one deck on its own (the panel)
uniform vec3 uGrad;                                                                           // height cue: share of the light left at a cloud's base, the curve (exponent on the openness), strength of the ambient part
// Fine structure below the cell (setDetail). Where a cell of the volume is several pixels wide, the density the model gave is carried on below it, as a cloud's edge is at every scale down to metres:
// it is eroded by a cascade of billowy noise (self-similar: the same noise at octave after octave, each half the size of the one above and warped by it, so lobes sit on lobes), weighted 2^(-H i), where
// the exponent H is the cloud's roughness (a small H keeps the small scales strong, a crisp cumulus; a large one lets them die away, a soft stratus). The noise is a tiling texture sampled in the cloud's
// own frame: the world minus uNoise, which the client carries on with the cloud wind, so the noise moves with the cloud and the same place in the world has the same noise in every ring. The erosion is not
// free water: the gain from uDetG (a table of the mean of the eroded extinction against the uneroded, per density and strength) puts back what the erosion takes, so a cell's mean extinction stays what
// the model made it. The cascade runs from a lobe of 267 m (bigger than a cell of the finest ring, so the cloud has lobes of its own size and not only grain) down to 33 m (a size of the cloud, so the billows do not change with the screen's resolution); an octave also fades out if its lobes shrink to a few pixels (far cloud), and the whole erosion fades out when the biggest lobe the level's own cells can carry (gateLobe) shrinks to a few pixels: every level erodes, each at the scales its cells leave open.
const float COARSE_WARP = ${COARSE_WARP.toFixed(3)}, COARSE_BEND = ${COARSE_BEND.toFixed(3)};
const float WORLD_BOOST = ${WORLD_BOOST.toFixed(2)};                                              // the world level's erosion is this much stronger: its 2 km blobs are what the eye sees from far up, and the shape is invented there
const int DET_OCT = ${DET_OCT}, DET_COARSE = ${DET_COARSE};                                                                  // octaves of the cascade (lobes of ${DET_L0.toFixed(0)} m, halving)
const float DET_P0 = ${DET_P0.toFixed(1)}, DET_L0 = ${DET_L0.toFixed(3)}, DET_PC = ${DET_PC.toFixed(1)}, DET_LC = ${DET_LC.toFixed(3)};                      // the first octave's period of the tiling texture and its lobe
const float NOISE_WRAP = 12800.0;                                                             // every period of the noise (1600 m halving, and the slow samples' 6400 and 12800 m) divides this: the noise frame is kept inside it
uniform sampler3D uDet; uniform sampler2D uDetG;
uniform vec4 uDetP;                                                                           // strength (0 for off), ramp start and end (pixels of a fine lobe), on
uniform vec2 uDetMS; uniform vec2 uNoise; uniform float uPixRad;                               // the noise texture's mean and spread; the noise's frame: world to noise frame (m); the angle a pixel covers (rad)
// The cascade at a noise-frame point g, as n in 0..1 (roughly uniform, mean a half) and the slow patchiness pat. Two slow samples (6400 and 12800 m) warp the point a
// little, so the tiling's grid does not show as rows, and a third (6400 m) says how textured the cloud is here: some stretches are smoother, some rougher. fp: the footprint of a pixel here (m).
// Octave i: lobes DET_L0 / 2^i, looked up at the point moved along its own direction (the golden angle) by the octave above's value, so the lobes sit on lobes; the sum is brought to unit
// spread and through a logistic to n.
float cascadeN(vec3 g, float H, float fp, float dxk, out float pat) {
  float wa = textureLod(uDet, g / 6400.0 + 0.31, 0.0).r, wb = textureLod(uDet, g / 12800.0 + 0.57, 0.0).r;
  pat = textureLod(uDet, g / 6400.0 + 0.12, 0.0).r;
  vec3 p = g + vec3(wa - 0.5, 0.7 * (wa + wb - 1.0), wb - 0.5) * 320.0;
  float S = 0.0, V = 0.0, zp = 0.0, L = DET_LC, P = DET_PC, w = 1.0, dw = exp2(-H);
  for (int i = 0; i < DET_COARSE + DET_OCT; i++) {
    bool coarse = i < DET_COARSE;
    float f = smoothstep(3.5, 8.0, L / fp);
    if (coarse) f *= 1.0 - smoothstep(1.2 * dxk, 2.4 * dxk, L);       // the three coarse octaves are for cloud whose own cells are as coarse as their lobes (the world level)
    else if (f <= 0.0) break;
    if (f > 0.0) {
      int ii = coarse ? i + 8 : i - DET_COARSE;
      float a = 2.39996 * float(ii), fi = float(ii + 1), sh = zp * (coarse ? COARSE_WARP : 0.45) * L;
      vec3 off = fract(vec3(0.618, 0.414, 0.732) * fi);
      vec3 bend = coarse ? vec3(wa - 0.5, 0.7 * (wa + wb - 1.0), wb - 0.5) * (COARSE_BEND * L) : vec3(0.0);      // (the coarse octaves are bent by the slow samples too, or their cells' straight creases show)
      float t = textureLod(uDet, (p + bend + vec3(cos(a), 0.6 * sin(a * 1.7), sin(a)) * sh) / P + off, 0.0).r;
      zp = (t - uDetMS.x) / uDetMS.y;
      float wi = w * f;
      S += wi * zp; V += wi * wi;
    }
    L *= 0.5; P *= 0.5; w *= dw;
  }
  return 1.0 / (1.0 + exp(-1.702 * S * inversesqrt(max(V, 1e-6))));
}

// The biggest lobe (m) the erosion of level k carries: the first fine octave (DET_L0), or the biggest coarse octave that counts in full for this level's cells (its lobe no bigger than 1.2 cells, as in
// cascadeN). The whole erosion of the level fades out as this lobe shrinks to a few pixels (the same rule for every level: a ring's cells are coarser than its fine octaves, so it keeps its coarse lobes in view
// far beyond where the 30 m billows are gone, and the world level keeps its 2 km ones).
float gateLobe(int k) {
  float best = DET_L0, L = DET_LC;
  for (int i = 0; i < DET_COARSE; i++) {
    if (L <= 1.2 * uDx[k]) best = max(best, L);
    L *= 0.5;
  }
  return best;
}

${LEVEL_FUNCS_GLSL}
// The baked light of level k at a point: R the sun beam's transmittance, G the sky's openness, B how rough the cloud is (0 soft .. 1 crisp).
vec3 readL(int k, vec3 pw) {
  vec4 z = uRgZ[k];
  float f = clamp(pw.y / uSpan * z.w, 0.5, z.w - 0.5);
  vec2 uv = levelUV(k, pw);
  if (k == 4) return textureLod(uLitW, vec3(uv, (z.z + f) / uZt.w), 0.0).rgb;
  return textureLod(uLitR, vec3(uv, (z.z + f) / uZt.y), 0.0).rgb;
}
// The block's height ranges of level k at a point, joined with what is in zlo.. so far: the cloud below and above 2.8 km are two ranges (a low cumulus and a layer aloft).
void rangesK(int k, vec3 pw, inout bool has, inout bool has2, inout float zlo, inout float zhi, inout float zlo2, inout float zhi2) {
  float nb = uRgM[k].z;
  vec2 bi = clamp(floor(levelUV(k, pw) * nb), vec2(0.0), vec2(nb - 1.0));
  ivec2 ib = ivec2(bi);
  vec4 ex = k == 4 ? texelFetch(uExtW, ib, 0) : texelFetch(uExtR, ivec2(ib.x, ib.y + int(uRgM[k].w)), 0);
  if (ex.g > ex.r) { has = true; zlo = min(zlo, ex.r * uSpan); zhi = max(zhi, ex.g * uSpan); }
  if (ex.a > ex.b) { has2 = true; zlo2 = min(zlo2, ex.b * uSpan); zhi2 = max(zhi2, ex.a * uSpan); }
}
// The rain of level k at a point: x the rate (mm/h), y the height of the cloud's base over it (m). The tables are linear in a level's own rows only (the read is kept half a texel inside them).
vec2 rainRB(int k, vec3 pw) {
  float n = uRgM[k].z * 2.0;                                                                  // texels across: twice the blocks (a texel is 2 columns, a block 4)
  vec2 c = clamp(levelUV(k, pw) * n, vec2(0.5), vec2(n - 0.5));
  vec4 s = k == 4 ? textureLod(uRainW, c / n, 0.0) : textureLod(uRainR, vec2(c.x / n, (c.y + uRgM[k].w * 2.0) / (n * 4.0)), 0.0);
  return vec2(s.r * s.r * uRainU.x, s.g * 255.0 * uRainU.y);
}
// The rain's extinction (1/m) at a point of level k. Marshall and Palmer drops at visible wavelengths take out about 0.365e-3 R^0.63 per metre (R in mm/h); the rain is there from the
// cloud's base down (it fades in over the last 250 m under it, where the cloud takes over) and it falls in streaks: the noise stretched along the fall thins it in places and thickens it in others.
float rainExt(int k, vec3 pw) {
  vec2 rb = rainRB(k, pw);
  if (rb.x < 0.01) return 0.0;
  float f = 1.0 - smoothstep(rb.y - 250.0, rb.y, pw.y);
  if (f <= 0.0) return 0.0;
  vec3 g = vec3(mod(pw.x - uNoise.x, NOISE_WRAP), pw.y, mod(pw.z - uNoise.y, NOISE_WRAP));
  float z = (textureLod(uDet, vec3(g.x / 800.0, g.y / 5000.0, g.z / 800.0) + 0.23, 0.0).r - uDetMS.x) / uDetMS.y;
  return uRainU.z * 3.65e-4 * pow(rb.x, 0.63) * f * clamp(1.0 + 0.45 * z, 0.35, 1.8);
}
// The extinction share of a sample whose density byte is dens (sqrt scale), in level k at world point pw, seen at range tm. The erosion comes in as the level's biggest lobe (gateLobe) grows past a few pixels.
// How much of it each cloud gets, and how it is spread over the scales, is the server's call: the light texture's blue channel says how rough this cloud is (0 a soft, diffuse sheet, 1 a crisp, lobed
// cumulus, from the weather's own vigour, depth, cover and the kind of deck). It sets the cascade's exponent (1.2 soft .. 0.6 crisp) and the strength (0.45 .. 1), so a stratus slab stays soft and
// a cumulus is cut to the finest lobes, and nothing is one texture everywhere.
float erodeX(float dens, vec3 pw, float tm, int k) {
  float x0 = dens * dens;
  if (uDetP.w < 0.5 || dens <= 0.0) return x0;
  float fp = tm * uPixRad, s = smoothstep(uDetP.y, uDetP.z, gateLobe(k) / fp);                // (every level's erosion lasts as long as the biggest lobe its cells leave open is a few pixels)
  if (s <= 0.0) return x0;
  float rg = readL(k, pw).b;                                                                  // the roughness here
  float rt = smoothstep(0.1, 0.75, rg);
  vec3 g = vec3(mod(pw.x - uNoise.x, NOISE_WRAP), pw.y, mod(pw.z - uNoise.y, NOISE_WRAP));
  float pt, n = cascadeN(g, mix(1.2, 0.6, rt), fp, uDx[k], pt);
  float k0 = clamp(uDetP.x * s * mix(0.45, 1.0, rt) * (0.55 + 0.9 * pt) * (k == 4 ? WORLD_BOOST : 1.0), 0.0, 1.0);
  float e = k0 * (1.0 - n);
  float r = clamp((dens - e) / (1.0 - e), 0.0, 1.0);
  float g2 = 1.0 + 3.0 * textureLod(uDetG, vec2((dens * 63.0 + 0.5) / 64.0, (clamp(k0, 0.0, 1.0) * 15.0 + 0.5) / 16.0), 0.0).r;
  float body = 1.0 + 0.6 * k0 * 2.0 * (n - 0.5) * smoothstep(0.15, 0.5, dens);                // inside the cloud the same noise makes lobes: lighter and denser by up to a third, zero on average
  return min(r * r * g2 * body, 1.5);
}
// The density share of level k at a point, eroded or not (er), and the same before the erosion (x0).
float shareAt(int k, vec3 pw, float tm, bool er, out float x0) {
  float dens = readD(k, pw);
  x0 = dens * dens;
  return er ? erodeX(dens, pw, tm, k) : x0;
}
// The density share (0..1, in uExtMax's scale) at a world point, as the march would find it: what the shading's taps look at. A blend of the levels that count there.
float tapDens(vec3 pw, float tm, bool er) {
  int k0, k1, k2; vec3 ws;
  int n = ringSet(pw, distance(pw, uCamRel), k0, k1, k2, ws);
  if (n == 0) return 0.0;
  float a, s = ws.x * shareAt(k0, pw, tm, er, a);
  if (n > 1) s += ws.y * shareAt(k1, pw, tm, er, a);
  if (n > 2) s += ws.z * shareAt(k2, pw, tm, er, a);
  return s;
}
varying vec3 vDir;
varying vec4 vPr;
uniform float uDepthS;                                                                        // 1: the depth written is the camera free one (DEPTH_K), for a march target; 0: the depth in the projection that is drawing
const float DEPTH_K = ${DEPTH_K.toFixed(1)};
${DEPTH_PACK_GLSL}
// Where a ray (from height y, going dy in height per metre of its length) is inside the layer zlo..zhi, as a range of its length (t0 .. t1, empty when t1 <= t0), at most tmax. This is a test of the ray
// against the volume's box, the data's own frame (the solver is flat, so the box has a height axis); it asks nothing of the eye: below, inside or above the box, looking any way.
vec2 slabRange(float y, float dy, float zlo, float zhi, float tmax) {
  if (abs(dy) < 1e-6) return (y < zlo || y > zhi) ? vec2(1.0, 0.0) : vec2(0.0, tmax);        // along the layer: all of the ray, or none of it
  float ta = (zlo - y) / dy, tb = (zhi - y) / dy;
  return vec2(max(0.0, min(ta, tb)), min(tmax, max(ta, tb)));
}
// The same on the planet (planet.ts): a straight ray is at the height y + dy s + c s^2 above the ground below it, c = (1 - dy^2) / 2R, because the ground falls away under it. The slab is the shell between
// the ground (height 0) and zhi: the ray ends where it meets the ground, and leaves the shell at the root of the quadratic (taken in the form that is good as c -> 0).
vec2 slabCurved(float y, float dy, float c, float zhi, float tmax) {
  float te = tmax;
  if (y > 0.0 && dy < 0.0) {
    float Dg = dy * dy - 4.0 * c * y;
    if (Dg >= 0.0) te = min(te, 2.0 * y / (-dy + sqrt(Dg)));
  }
  if (y <= zhi) {
    float D = dy * dy + 4.0 * c * (zhi - y);
    return vec2(0.0, min(te, 2.0 * (zhi - y) / max(dy + sqrt(D), 1e-9)));
  }
  float D = dy * dy - 4.0 * c * (y - zhi);                                                    // above the shell: the ray has to come down into it, and it leaves again where the ground's fall lifts it out
  if (dy >= 0.0 || D < 0.0) return vec2(1.0, 0.0);
  float s = sqrt(D);
  return vec2(2.0 * (y - zhi) / (-dy + s), min(te, (-dy + s) / (2.0 * c)));
}
// How far along a ray whose height now has the slope sl (per metre of ray) and curves up by cA s^2 until it has risen by dz (> 0), or fallen by dz; 1e9 when it never does. The slope is kept off zero
// as the flat march always kept it (no step is asked for that is over a thousand times dz).
float lenRise(float dz, float sl, float cA) {
  float den = sl + sqrt(sl * sl + 4.0 * cA * dz);
  return den > 1e-6 ? 2.0 * dz / max(den, 2e-3) : 1e9;
}
float lenFall(float dz, float sl, float cA) {
  float D = sl * sl - 4.0 * cA * dz;
  return (sl < 0.0 && D >= 0.0) ? 2.0 * dz / max(-sl + sqrt(D), 2e-3) : 1e9;
}
// the same for the point of the ray at length tt (on the planet the ray's height is lifted by the ground's fall, as the data is read: see main)
float rockAt(vec3 cam, vec3 d, float cA, float tt) {
  vec3 p = cam + d * tt;
  p.y += cA * tt * tt;
  return aboveGround(p);
}
void main() {
  gl_FragDepth = 1.0;                                                                         // set on every path (a pixel with no cloud is discarded)
  if (uAny < 0.5) discard;
  vec3 d = normalize(vDir);
  vec3 cam = uCamRel;
  float zlo0 = 0.0, zhi0 = uSpan;
  float cA = uPlanet.x > 0.0 ? (1.0 - d.y * d.y) * (0.5 / uPlanet.x) : 0.0;                   // the ground falls away under the ray by cA s^2 (0: flat); a sample's height is its y lifted by it
  vec2 tr = cA > 0.0 ? slabCurved(cam.y, d.y, cA, zhi0, max(uMaxD, uMaxA))
                     : slabRange(cam.y, d.y, zlo0, zhi0, max(uMaxD, uMaxA));                // (the longer of the two reaches: the low cloud is cut at uMaxD below, and only the layers aloft go on past it)
  float t0 = tr.x, t1 = tr.y;
  if (t1 <= t0) discard;
  float hxz = max(length(d.xz), 1e-3);
  float jit = mix(0.5, hash12(gl_FragCoord.xy + uFrame * 5.0), uJit);
  float t = t0, Tr = 1.0, wsum = 0.0, beam = 0.0, ambA = 0.0, tmean = 0.0, n3 = 0.0, hsum = 0.0, tmW = 0.0, tS = 0.0;
  float fineUntil = -1.0, lastC = 0.0, grow = 1.0, prevT = t0, prevG = 1.0, noGrow = -1.0, nIt = 0.0;
  // The ground ends the ray: the march knows the sea level only (slabRange), and the rock is not air, so what lies behind the surface is not drawn (a ray into a slope used to go on through it, and the
  // cloud it met there, drawn against the terrain's depth, made stairs along the rows of equal distance). tChk: how far along the ray the point is known to be above the ground; tGround: where it ended.
  bool gOn = uGndMax > 0.5;
  float tChk = t0, tGround = 1e9;
  if (gOn && rockAt(cam, d, cA, t0) < -0.05) gOn = false;                                     // an eye inside the rock: this ray is left as it was
  for (int i = 0; i < 220; i++) {
    nIt += 1.0;
    if (float(i) >= uMaxSteps || t > t1 || Tr < 0.03) break;
    vec3 pw = cam + d * t;
    pw.y += cA * t * t;                                                                       // (the height above the curved ground, planet.ts; the data is read at it)
    float sl = d.y + 2.0 * cA * t;                                                            // the height's slope along the ray here
    if (t > uMaxD && pw.y < 2800.0) { float up = lenRise(2800.0 - pw.y, sl, cA); if (up > 1e8) break; t += max(50.0, up); continue; }     // past the low cloud's horizon only the layers aloft count: go up to them (a ray that does not climb never gets there)
    float dmax = mix(uMaxD, uMaxA, smoothstep(2800.0, 3800.0, pw.y));
    // Which levels' ranges apply here: those that count at this point (the finest of them sets the size of the steps).
    int k0, k1, k2; vec3 ws;
    int nr = ringSet(pw, t, k0, k1, k2, ws);
    float zlo = 1e9, zhi = -1e9, zlo2 = 1e9, zhi2 = -1e9;
    bool has = false, has2 = false;                                                           // the block's cloud below and above 2.8 km (a low cumulus and a layer aloft are two ranges)
    if (nr > 0) rangesK(k0, pw, has, has2, zlo, zhi, zlo2, zhi2);
    if (nr > 1) rangesK(k1, pw, has, has2, zlo, zhi, zlo2, zhi2);
    if (nr > 2) rangesK(k2, pw, has, has2, zlo, zhi, zlo2, zhi2);
    float blockM = uRg[k0].z / uRgM[k0].z;
    bool gapNow = has && has2 && pw.y > zhi && pw.y < zlo2;                                  // in the clear air between the two ranges steps may grow; anywhere else they may not: a step longer than a thin bright fringe jumps over it, and the pixel shows the dark body behind it (the pinholes along a lobe's edge)
    if (t >= fineUntil && !((has && pw.y >= zlo && pw.y <= zhi) || (has2 && pw.y >= zlo2 && pw.y <= zhi2))) {   // outside the block's cloud: a coarse step
      if (gOn && t > tChk) { if (rockAt(cam, d, cA, t) < -0.05) { tGround = t; break; } tChk = t; }   // (the ray is in the rock, outside any cloud: it ends)
      float dt = min(0.5 * blockM / hxz, 4000.0);
      float nd = 1e9;                                                                         // the length of ray to the nearest range of cloud ahead (a ray that climbs meets a range's floor, one that descends its top; on the planet a level ray also climbs, lenRise)
      if (has && pw.y < zlo) nd = lenRise(zlo - pw.y, sl, cA);
      if (has2 && pw.y < zlo2) nd = min(nd, lenRise(zlo2 - pw.y, sl, cA));
      if (has && pw.y > zhi) nd = min(nd, lenFall(pw.y - zhi, sl, cA));
      if (has2 && pw.y > zhi2) nd = min(nd, lenFall(pw.y - zhi2, sl, cA));
      if (nd < 1e8) dt = min(dt, max(nd, 4.0));
      lastC = max(dt, 4.0);
      t += lastC;
      continue;
    }
    if (lastC > 0.0) {                                                                        // a coarse step has just landed in cloud: it may have jumped over the soft edge (a block-sized
      float tb = max(t - lastC, t0);                                                          // ladder across the cloud), so go back to where it started and march that stretch finely
      fineUntil = t; lastC = 0.0; t = tb;
      continue;
    }
    if (gOn && t > tChk) { if (rockAt(cam, d, cA, t) < -0.05) { tGround = t; break; } tChk = t; }       // (reached by a coarse step or a jump: the ground may have come first)
    float vx = uDx[k0];
    float capC = k0 == 4 ? mix(0.3, 0.7, smoothstep(12000.0, 30000.0, t)) : 0.3;                 // the world level's steps may grow with range: far out its 2 km cells are tens of pixels wide, a third of one is far finer than the picture can show, and the rays that run along the horizon spent the whole budget there
    float dt = max(min(vx * uFine, max(uStepMax, capC * vx)), t * 0.0022 * uPx);                  // (the longest step in cloud is for the finest ring's cells: a coarser level's cells are wider than that, so its steps are a third of a cell at most, up to 0.7 of the world level's from 12 to 30 km out)
    dt = max(dt, uFine * t * uFar.x);                                                         // the wider filter of the probe, and far out: steps in proportion to the range
    if (uDetP.w > 0.5) {                                                                      // where the erosion is on, the steps follow the pixels, so the structure it makes is sampled (3.5 pixels of footprint, and not under 15% of a cell) and not stepped over
      float s0 = smoothstep(uDetP.y, uDetP.z, gateLobe(k0) / (max(t, 1.0) * uPixRad));
      dt = min(dt, mix(dt, max(3.5 * max(t, 1.0) * uPixRad, max(0.15 * vx, 5.0)), s0));
    }
    {                                                                                         // under a cloud's base the air holds only rain, which is smooth: the steps are a cell and a half at least
      vec2 rb = rainRB(k0, pw);
      if (rb.x >= 0.01 && pw.y < rb.y - 150.0) dt = max(dt, 0.8 * vx);
    }
    float dt0 = dt; dt *= grow;                                                               // in clear air inside a block's range (the gap between a low cloud and a layer aloft) the steps grow
    bool hitG = false;
    if (gOn) {                                                                                // a step that ends in the rock is cut at the surface (found by halving), so the cloud's last stretch is the length that is in the air
      if (rockAt(cam, d, cA, t + dt) < -0.05) {
        float g0 = t, g1 = t + dt;
        for (int b = 0; b < 6; b++) { float gm = 0.5 * (g0 + g1); if (rockAt(cam, d, cA, gm) < -0.05) g1 = gm; else g0 = gm; }
        dt = max(g0 - t, 1e-3);
        hitG = true;
      } else tChk = max(tChk, t + dt);
    }
    float tStart = t;
    float tm = t + dt * (0.25 + 0.5 * jit);
    vec3 qw = cam + d * tm;
    qw.y += cA * tm * tm;
    int m0, m1, m2; vec3 mw;
    int nm = ringSet(qw, tm, m0, m1, m2, mw);
    if (nm > 0) {
      n3 += 1.0;
      float r0 = 0.0, r1 = 0.0, r2 = 0.0, x1 = 0.0, x2 = 0.0;
      float x0 = shareAt(m0, qw, tm, true, r0);
      if (nm > 1) x1 = shareAt(m1, qw, tm, true, r1);
      if (nm > 2) x2 = shareAt(m2, qw, tm, true, r2);
      if (uSteep != 1.0) { if (x0 > 0.0) x0 = pow(x0, uSteep); if (x1 > 0.0) x1 = pow(x1, uSteep); if (x2 > 0.0) x2 = pow(x2, uSteep); }
      float clipM = step(uClip.x, qw.y) * step(qw.y, uClip.y);
      float e0 = mw.x * x0 * clipM, e1 = mw.y * x1 * clipM, e2 = mw.z * x2 * clipM;
      float xd = e0 + e1 + e2;                                                                // between the levels the extinctions are blended (the water of two clouds is not added up)
      float xr = mw.x * rainExt(m0, qw);                                                      // the rain under the cloud, in the same unit as a share (blended the same way)
      if (nm > 1) xr += mw.y * rainExt(m1, qw);
      if (nm > 2) xr += mw.z * rainExt(m2, qw);
      xr *= clipM / uExtMax;
      float xsh = xd;                                                                         // the density share the shading compares
      float xshB = (mw.x * r0 + mw.y * r1 + mw.z * r2) * clipM;                              // (the same before the erosion: what the wider taps compare against)
      float e = (xd + xr) * uExtMax * (1.0 - smoothstep(uFar.y * dmax, dmax, tm));           // the cloud thins out toward the draw distance instead of ending there
      if (e > 2e-6) {
        float a = 1.0 - exp(-e * dt);
        float wt = Tr * a;
        vec2 lg = vec2(0.0);                                                                  // each level's light, weighted by the extinction it brings
        if (e0 > 0.0) lg += e0 * readL(m0, qw).rg;
        if (e1 > 0.0) lg += e1 * readL(m1, qw).rg;
        if (e2 > 0.0) lg += e2 * readL(m2, qw).rg;
        if (xr > 0.0) lg += xr * readL(m0, qw).rg;                                            // rain is lit as the air it falls through is
        lg /= max(xd + xr, 1e-12);
        n3 += 1.0;
        if (prevG > 1.05 && t >= noGrow) { noGrow = t + dt; t = prevT; grow = 1.0; prevG = 1.0; continue; }   // cloud found after a grown step: go back and march that stretch finely
        float fsh = 1.0;                                                                      // the sun-side shading, below
        // Multi-scale sun-side shading: taps toward the light at 3 growing distances (in pixels of footprint, so every range shows lobes of the same apparent size); where the density there is
        // lower than here the surface at that scale faces the light (lit), where it is higher a neighbour shades it. Only for samples that count (the surface, not the cloud behind it).
        float fx = uShade * smoothstep(uFx2.z, uFx2.w, tm);
        if (fx > 0.001 && uTaps > 0.5 && wt > 0.03 && xd > 0.0) {
          float Lk = max(uFx2.x * tm * 0.0016, 25.0), lob = 1.0;
          for (int k = 0; k < 3; k++) {
            if (float(k) >= uTaps) break;
            float ds = tapDens(qw + uLDir * Lk, tm, k == 0);
            float dv = clamp(((k == 0 ? xsh : xshB) - ds) * 5.0, -1.0, 1.0);
            lob *= 1.0 + fx * (1.0 - 0.2 * float(k)) * (dv > 0.0 ? 0.5 : 0.7) * dv;
            Lk *= uFx2.y; n3 += k == 0 ? 3.0 : 2.0;
          }
          fsh *= clamp(lob, 0.25, 2.0);
        }
        { float wn = wsum + wt, dl = tm - tmW; tmW += dl * wt / max(wn, 1e-12); tS += wt * dl * (tm - tmW); }       // the spread of the cloud's weight along the ray (a running weighted variance)
        beam += wt * lg.x * fsh; ambA += wt * (0.35 + 0.45 * lg.y) * mix(1.0, fsh, 0.35); tmean += wt * tm; wsum += wt;
        hsum += wt * lg.y;                                                                    // the sky's openness above the point: 1 at a cloud's top, 0 under a thick body (smooth: it is baked and filtered, no block edges)
        Tr *= 1.0 - a;
        grow = 1.0;
      } else grow = (gapNow && t >= noGrow) ? min(grow * 1.35, uGrowMax) : 1.0;
    } else grow = (gapNow && t >= noGrow) ? min(grow * 1.35, uGrowMax) : 1.0;
    prevT = tStart; prevG = grow > 1.0 ? dt / max(dt0, 1e-3) : 1.0;
    t += dt;
    if (hitG) { tGround = t; break; }
  }
  float alpha = 1.0 - Tr;
  if (uDebug > 0.5) { gl_FragColor = vec4(uDebug > 2.5 ? nIt / 220.0 : n3 / (uDebug > 1.5 ? 1024.0 : 256.0), 0.0, 0.0, 1.0); return; }
  if (alpha < 0.003) discard;
  float iw = 1.0 / max(wsum, 1e-6);
  tmean *= iw;
  {                                                                                           // the depth of the cloud here: the mean length of its ray, weighted by what each stretch adds to the picture
    float tH = max(tmean, 1e-3);
    // Where the cloud of this pixel starts and ends: the mean of its weight along the ray, and a stretch of the width a uniform spread of the same variance has. The composite draws it as slices over that
    // stretch, so the scene that stands inside the cloud (the plants) is veiled by the cloud in front of it only, not all or nothing.
    float hw = 1.7320508 * sqrt(max(tS * iw, 0.0));
    float tF = max(tH - hw, max(t0, 1e-3)), tB = tH + hw;
    if (tGround < 1e8) {                                                                       // a ray that ended on the ground: its cloud is in front of the surface, by a margin that the depth test (against the terrain's own depth) cannot flip
      float tg = max(tGround - max(2.0, 0.004 * tGround), 1e-3);
      tH = min(tH, tg); tB = min(tB, tg); tF = min(tF, tB);
    }
    if (uDepthS > 0.5) gl_FragDepth = packDepth(tF, tB);
    else { float zv = -tH / length(vDir); gl_FragDepth = clamp(((vPr.z * zv + vPr.w) / -zv) * 0.5 + 0.5, 0.0, 1.0); }
  }
  float hr = hsum * iw;                                                                       // how open to the sky the pixel's cloud is: 0 under a thick body (a base), 1 at a top
  float shade = mix(uGrad.x, 1.0, pow(clamp(hr, 0.0, 1.0), uGrad.y));                                  // the eye reads light from above: the top is lit, the base dark, all through the structure
  shade = mix(1.0, shade, smoothstep(0.02, 0.35, alpha));                                 // a veil or a faint edge has no body to be darker at the bottom
  // the ambient light's direction (ambientSh.ts): a base looks down at the ground, a top up at the sky, the field says how much light each has; it does the height cue's work on the ambient, so that cue's share fades as the field comes in
  vec3 col = cloudColor(beam * iw, ambA * iw * mix(1.0, shade, uGrad.z * (1.0 - uShMix)), shade, d, tmean * uFar.z, ambField(d, hr));                        // uFar.z scales the distance the air is read over (1: as it is)
  gl_FragColor = vec4(col, clamp(alpha, 0.0, 1.0));
  #include <colorspace_fragment>
}`;

/** The march's default size against the screen's, each way: a quarter of the pixels. ?cloudscale=1 draws it at full size again (see CloudVolume.setScale). */
export const SMALL_DEFAULT = 0.5;

// The stretch of the small target back over the screen. The quad sits at the far plane and is depth-tested, so whatever is drawn in front of the sky
// (the land, trees, rocks) still hides the cloud with its own full-size edge. The target holds colour times alpha: the filter averages that, and the
// colour is recovered here, then encoded for the screen like any other output (the march wrote it unencoded: a target is linear).
const COMP_VERT = /* glsl */`
varying vec2 vUv;
varying vec4 vPr;
varying float vLayer;
void main() {
  vUv = position.xy * 0.5 + 0.5;
  vPr = vec4(projectionMatrix[0][0], projectionMatrix[1][1], projectionMatrix[2][2], projectionMatrix[3][2]);
  vLayer = float(gl_InstanceID);                                                             // the quad is drawn COMP_LAYERS times in one call: instance j is slice j of the pixel's cloud
  gl_Position = vec4(position.xy, 0.9999, 1.0);
}`;
// The composite draws each pixel's cloud as COMP_LAYERS slices, one per instance of the quad. The march stored where the pixel's cloud starts and ends (tf, tb: packDepth); the cloud's weight is taken
// as spread evenly between them, so slice j (a share 1 / COMP_LAYERS of that stretch) sits at its middle and carries the share of the pixel's alpha that lies there. Each slice is depth-tested against
// the scene at the screen's full resolution, as the one quad was: the slices in front of a plant show over it, the ones behind it do not, so a crown that stands in a fog bank is veiled by the fog in
// front of it (where it used to be all of it or none of it, by whether the cloud's mean depth was in front or behind). All slices have the same colour, so the order they blend in does not matter, and the
// alphas are chosen so that the first k slices add up to k / COMP_LAYERS of the pixel's alpha (the rest, in front of which the scene stands, is cut). The depth is read from the four march texels
// round the pixel, at the place each was marched through (uOff: the march's jitter), and averaged by the bilinear weights over the texels whose cloud is at about the nearest one's depth (a smooth
// surface has no blocks, and across an edge the nearer cloud wins: the colour is a blend of those four, and the cloud that shows must not be hidden by a neighbour that has none). No cloud among them:
// one slice, behind everything.
const COMP_FRAG = /* glsl */`
precision highp float; precision highp sampler2D;
uniform sampler2D uTex, uDepth; uniform float uOn, uNearFade, uLayers; uniform vec2 uDsz, uOff;
${HANDOVER_GLSL}
varying vec2 vUv;
varying vec4 vPr;
varying float vLayer;
const float DEPTH_K = ${DEPTH_K.toFixed(1)};
${DEPTH_PACK_GLSL}
#define LAYERS uLayers                                               // the slices drawn (setLayers; instances of the quad)
void main() {
  vec4 s = texture2D(uTex, vUv);
  if (uOn < 0.5 || s.a < 0.002) discard;
  vec2 fc = (vUv - uOff) * uDsz - 0.5;
  ivec2 i0 = ivec2(floor(fc)), hi = ivec2(uDsz) - 1;
  vec2 fr = fc - floor(fc);
  float pk[4];
  float pm = 1.0;
  for (int k = 0; k < 4; k++) { pk[k] = texelFetch(uDepth, clamp(i0 + ivec2(k & 1, k >> 1), ivec2(0), hi), 0).r; pm = min(pm, pk[k]); }
  float dep = 0.9999;
  float hs = 1.0;                                                    // the near field's share at this cloud: by its range and its distance from the patch's centre (handover.ts)
  bool have = pm < 0.99999;
  if (!have && vLayer > 0.5) discard;
  if (have) {
    float rf, rb;
    unpackDepth(pm, rf, rb);
    float rm = 0.5 * (rf + rb), ws = 0.0, af = 0.0, ab = 0.0;
    for (int k = 0; k < 4; k++) {
      if (pk[k] >= 0.99999) continue;
      float kf, kb;
      unpackDepth(pk[k], kf, kb);
      if (abs(0.5 * (kf + kb) - rm) > 0.25 * rm + 2.0) continue;
      float w = (((k & 1) == 1) ? fr.x : 1.0 - fr.x) * (((k >> 1) == 1) ? fr.y : 1.0 - fr.y);
      ws += w; af += w * kf; ab += w * kb;
    }
    float tf = rf, tb = rb;
    if (ws > 1e-4) { tf = af / ws; tb = ab / ws; }
    float tl = tf + (vLayer + 0.5) / LAYERS * (tb - tf);             // this slice's distance
    float tmid = 0.5 * (tf + tb);
    vec2 ndc = vUv * 2.0 - 1.0;
    vec3 vr = vec3(ndc.x / vPr.x, ndc.y / vPr.y, -1.0);
    float zv = -tl / length(vr);
    dep = clamp(((vPr.z * zv + vPr.w) / -zv) * 0.5 + 0.5, 0.0, 1.0);
    if (uHand.x > 0.0) {
      vec3 wp = cameraPosition + (vec4(vr * (tmid / length(vr)), 0.0) * viewMatrix).xyz;   // where the cloud is, in the tile's own axes (x east, z south)
      hs = handRange(tmid) * handPatch(vec2(wp.x, -wp.z));
    }
  }
  float A = min(s.a, 1.0) * uNearFade * hs;                         // (uNearFade: the flat tile's share, space.ts nfc; hs: the share the shell leaves it, handover.ts)
  float al = have ? (A / LAYERS) / max(1.0 - A * vLayer / LAYERS, 1e-4) : A;
  gl_FragDepth = dep;
  gl_FragColor = vec4(s.rgb / s.a, min(al, 1.0));
  #include <colorspace_fragment>
}`;

// The temporal march (CloudVolume.renderView). Each frame the march draws one pixel in four of the small target (a different one each frame, in a fixed order), and this
// pass builds the small target from them and from the last one: every pixel is last frame's picture, looked up where this pixel was (the camera has turned a little), and the
// pixel that was marched this frame is mostly replaced by its fresh value. Cloud is soft and slow, so four frames of it hold a picture as good as one marched whole, at a
// quarter of the cost. Where the camera moves the old picture is held inside the range of the fresh samples around it (no trails), and a change of light takes it over at once.
const RES_VERT = /* glsl */`
void main() { gl_Position = vec4(position.xy, 0.0, 1.0); }`;
const RES_FRAG = /* glsl */`
precision highp float; precision highp sampler2D;
uniform sampler2D uFresh, uHist, uFreshD;
uniform vec2 uSize, uPar, uProj, uProjPrev;
uniform mat3 uView, uViewPrev;
uniform vec3 uCamD;                                                                          // the eye's move since the last frame (worked out in doubles)
uniform float uReset, uAge, uBoost;
const float DEPTH_K = ${DEPTH_K.toFixed(1)};
${DEPTH_PACK_GLSL}
void main() {
  ivec2 ip = ivec2(gl_FragCoord.xy);
  vec2 uv = gl_FragCoord.xy / uSize;
  vec2 off = (uPar - 0.5) / uSize;                                                          // the fresh texel (i, j) was marched through the pixel (2i + ox, 2j + oy), not through the middle of the four
  vec2 fsz = vec2(textureSize(uFresh, 0));
  vec4 cur = texture2D(uFresh, uv - off);
  bool exact = (ip.x & 1) == int(uPar.x) && (ip.y & 1) == int(uPar.y);
  if (exact) cur = texelFetch(uFresh, ip >> 1, 0);
  vec2 ndc = uv * 2.0 - 1.0;
  vec3 dw = normalize(vec3(ndc.x / uProj.x, ndc.y / uProj.y, -1.0) * uView);                // this pixel's direction in the world
  // Where the cloud this pixel shows was, seen from the last frame's eye: the point at the distance the fresh samples around it found (the nearest of the four; none: the cloud is far
  // enough, or absent, and only the direction counts), looked up from where the eye was. The eye's own move is what parallax is.
  float sd = 1.0;
  {
    vec2 fc = (uv - off) * fsz - 0.5;
    ivec2 j0 = ivec2(floor(fc)), hj = ivec2(fsz) - 1;
    for (int k = 0; k < 4; k++) sd = min(sd, texelFetch(uFreshD, clamp(j0 + ivec2(k & 1, k >> 1), ivec2(0), hj), 0).r);
  }
  float tw = 0.0;
  if (sd < 0.99999) { float rf, rb; unpackDepth(sd, rf, rb); tw = 0.5 * (rf + rb); }                  // (the middle of the stretch the cloud covers: where the picture's weight is)
  vec3 rel = sd < 0.99999 ? dw * tw + uCamD : dw;
  vec3 vp = uViewPrev * rel;
  bool ok = uReset < 0.5 && vp.z < -1e-4;
  vec2 uvP = vec2(-1.0);
  if (ok) {
    uvP = vec2(vp.x / -vp.z * uProjPrev.x, vp.y / -vp.z * uProjPrev.y) * 0.5 + 0.5;
    ok = all(greaterThan(uvP, vec2(0.0))) && all(lessThan(uvP, vec2(1.0)));
  }
  if (!ok) { gl_FragColor = cur; return; }
  vec4 h = texture2D(uHist, uvP);
  float sh = length((uvP - uv) * uSize);                                                     // how far the lookup moved, in pixels
  if (sh > 0.05) {
    vec2 fc = (uv - off) * fsz - 0.5;
    ivec2 i0 = ivec2(floor(fc)), hi = ivec2(fsz) - 1;
    vec4 a = texelFetch(uFresh, clamp(i0, ivec2(0), hi), 0), b = texelFetch(uFresh, clamp(i0 + ivec2(1, 0), ivec2(0), hi), 0);
    vec4 c = texelFetch(uFresh, clamp(i0 + ivec2(0, 1), ivec2(0), hi), 0), d = texelFetch(uFresh, clamp(i0 + ivec2(1, 1), ivec2(0), hi), 0);
    vec4 mn = min(min(a, b), min(c, d)), mx = max(max(a, b), max(c, d)), rg = mx - mn;
    h = mix(h, clamp(h, mn - 0.25 * rg - 0.02, mx + 0.25 * rg + 0.02), smoothstep(0.05, 1.0, sh));
  }
  float age = smoothstep(0.0, 8.0, uAge);
  float a = exact ? mix(1.0, 0.5, age) : 0.04 + 0.3 * smoothstep(0.1, 2.0, sh);
  a = max(a, uBoost * (exact ? 1.0 : 0.6));
  gl_FragColor = mix(h, cur, a);
}`;

// ---- helpers ----------------------------------------------------------------------------------------------------------------------------

function b64bytes(s: string): Uint8Array {
  const bin = atob(s), out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** Inflate zlib bytes (what the server's zlib.compress makes) with the browser's own decompressor. */
async function inflate(bytes: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(new DecompressionStream('deflate'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/** The baked light (two planes of levels, direct then sky, `cells` each) as the RGBA bytes the texture holds: R the direct, G the sky, B the roughness of the cloud in the cell
 *  (`rough`, one byte a cell; 255, full detail as before roughness existed, when there is none), A 255, each level scaled by `k`. The roughness rides in the light's texture because it has the same grid and
 *  the same frame, so the blend, the roll into a new frame and the upload treat it as they treat the light. A table of the 256 possible values and one 32 bit store a cell,
 *  where the plain loop rounded and stored four bytes a cell (the same bytes). */
export function litToRgba(lit: Uint8Array, cells: number, k: number, rough?: Uint8Array | null): Uint8Array {
  const rgba = new Uint8Array(cells * 4), lut = new Uint8Array(256);
  const rb = rough && rough.length >= cells ? rough : null;
  for (let v = 0; v < 256; v++) lut[v] = Math.round(v * k);                  // a byte store, as the plain loop's, so a value over 255 wraps the same way
  if (new Uint8Array(new Uint32Array([1]).buffer)[0] === 1) {
    const w = new Uint32Array(rgba.buffer);
    for (let i = 0; i < cells; i++) w[i] = lut[lit[i]!]! | (lut[lit[cells + i]!]! << 8) | ((rb ? rb[i]! : 255) << 16) | 0xFF000000;
  } else {
    for (let i = 0; i < cells; i++) { rgba[4 * i] = lut[lit[i]!]!; rgba[4 * i + 1] = lut[lit[cells + i]!]!; rgba[4 * i + 2] = rb ? rb[i]! : 255; rgba[4 * i + 3] = 255; }
  }
  return rgba;
}

/** Let the browser draw a frame (or handle input) before the next stage of a long job. A message channel, not a timer: timers in a hidden tab wait a second or more (and a minute
 *  once the tab has been hidden a while), which would hold a message back for that long at every stage. */
const yieldToFrame: () => Promise<void> = (() => {
  if (typeof MessageChannel === 'undefined') return () => new Promise<void>(r => setTimeout(r, 0));
  const ch = new MessageChannel();
  let waiting: (() => void)[] = [];
  ch.port1.onmessage = () => { const w = waiting; waiting = []; for (const f of w) f(); };
  (ch.port1 as unknown as { unref?: () => void }).unref?.();                  // (Node only) do not keep a process alive for it
  return () => new Promise<void>(r => { waiting.push(r); ch.port2.postMessage(0); });
})();


/** o = a + (b - a) k / 256 over bytes, rounded (k = 0 is a, 256 is b). Four bytes at a time when the arrays allow (two 16 bit lanes per half word: a j + b k is at
 *  most 255 x 256 plus the rounding, which fits a lane), about three times faster than byte by byte. */
function lerp8(a: Uint8Array, b: Uint8Array, o: Uint8Array, k: number): void {
  if (k <= 0) { o.set(a); return; }
  if (k >= 256) { o.set(b); return; }
  const j = 256 - k, n = o.length;
  if ((n & 3) === 0 && ((a.byteOffset | b.byteOffset | o.byteOffset) & 3) === 0) {
    const A = new Uint32Array(a.buffer, a.byteOffset, n >> 2), B = new Uint32Array(b.buffer, b.byteOffset, n >> 2), O = new Uint32Array(o.buffer, o.byteOffset, n >> 2);
    for (let i = 0, m = n >> 2; i < m; i++) {
      const x = A[i], y = B[i];
      const lo = (Math.imul(x & 0x00FF00FF, j) + Math.imul(y & 0x00FF00FF, k) + 0x00800080) >>> 8 & 0x00FF00FF;
      const hi = (Math.imul((x >>> 8) & 0x00FF00FF, j) + Math.imul((y >>> 8) & 0x00FF00FF, k) + 0x00800080) & 0xFF00FF00;
      O[i] = lo | hi;
    }
    return;
  }
  for (let i = 0; i < n; i++) o[i] = (a[i] * j + b[i] * k + 128) >> 8;
}

/** The stretches (pairs [start, end) of 32 bit words) of the first `m` bytes where `a` and `b` differ; stretches less than RUN_GAP words apart are joined. Two consecutive
 *  volumes of the same cloud differ in a few per cent of their bytes (measured on real publishes: 0.8% of the baked light, 3% of the density, 7% of the window), and a blend
 *  of two equal bytes is that byte, so the cross-fade only has to work on these stretches. null when the arrays cannot be walked as words (then the whole array is blended). */
const RUN_GAP = 8;                 // measured on real publishes (gaps of 0 to 64 words): 8 gives the cheapest step, 0.8 ms against 5.9 ms for the whole-array lerp
export function diffRuns(a: Uint8Array, b: Uint8Array, m: number, gap = RUN_GAP): Uint32Array | null {
  if ((m & 3) !== 0 || ((a.byteOffset | b.byteOffset) & 3) !== 0 || a.length < m || b.length < m) return null;
  const w = m >> 2, A = new Uint32Array(a.buffer, a.byteOffset, w), B = new Uint32Array(b.buffer, b.byteOffset, w);
  const out: number[] = [];
  let start = -1, last = -1;
  for (let i = 0; i < w; i++) {
    if (A[i] !== B[i]) {
      if (start < 0) start = i;
      else if (i - last > gap) { out.push(start, last + 1); start = i; }
      last = i;
    }
  }
  if (start >= 0) out.push(start, last + 1);
  return Uint32Array.from(out);
}

/** lerp8 over the stretches `runs` only (see diffRuns). Gives the same bytes as lerp8 over the whole array when everything outside the stretches is equal in `a` and `b` and already in `o`.
 *  All three arrays must be 4 byte aligned. */
export function lerp8Runs(a: Uint8Array, b: Uint8Array, o: Uint8Array, runs: Uint32Array, k: number): void {
  const A = new Uint32Array(a.buffer, a.byteOffset, a.length >> 2), B = new Uint32Array(b.buffer, b.byteOffset, b.length >> 2), O = new Uint32Array(o.buffer, o.byteOffset, o.length >> 2);
  const j = 256 - Math.min(256, Math.max(0, k)), kk = 256 - j;
  for (let r = 0; r < runs.length; r += 2) {
    for (let i = runs[r]!, e = runs[r + 1]!; i < e; i++) {
      const x = A[i]!, y = B[i]!;
      const lo = (Math.imul(x & 0x00FF00FF, j) + Math.imul(y & 0x00FF00FF, kk) + 0x00800080) >>> 8 & 0x00FF00FF;
      const hi = (Math.imul((x >>> 8) & 0x00FF00FF, j) + Math.imul((y >>> 8) & 0x00FF00FF, kk) + 0x00800080) & 0xFF00FF00;
      O[i] = lo | hi;
    }
  }
}


/** The window the client showed (`from`: `layers` layers of n x n cells, `es` bytes a cell: 1, or 4 for RGBA) seen from the next message's window, which is dcx columns to the east and dcy rows to the
 *  south of it: the same cloud at the same place, out[k][r][c] = from[k][r + dcy][c + dcx]. Where the old window does not reach (a strip along two edges, as wide as the window has moved) the new
 *  window's own cells stand (`to`), so the fade there is from the new picture to itself. The result is a new array. */
export function shiftWin(from: Uint8Array, to: Uint8Array, n: number, layers: number, es: 1 | 4, dcx: number, dcy: number): Uint8Array {
  const out = to.slice();
  const c0 = Math.max(0, -dcx), c1 = Math.min(n, n - dcx);
  if (c1 <= c0 || Math.abs(dcy) >= n) return out;
  const A: any = es === 4 ? new Uint32Array(from.buffer, from.byteOffset, from.length >> 2) : from, O: any = es === 4 ? new Uint32Array(out.buffer) : out;
  for (let k = 0; k < layers; k++) {
    for (let r = 0; r < n; r++) {
      const rr = r + dcy;
      if (rr < 0 || rr >= n) continue;
      const src = (k * n + rr) * n + c0 + dcx;
      O.set(A.subarray(src, src + (c1 - c0)), (k * n + r) * n + c0);
    }
  }
  return out;
}

/** The difference of two window origins (in blocks) across the world's seam: the shortest way round. */
const wrapBlocks = (d: number, w: number) => (((d + w / 2) % w) + w) % w - w / 2;

/** The union of two extent tables (the server's per block height ranges: R G below 2.8 km, B A above; a range with its top not above its bottom is empty): where either has cloud, the
 *  range that holds both. The march skips what the table calls empty, so a table that is too small cuts cloud off and one that is too big only costs steps. */
export function unionExt(a: Uint8Array, b: Uint8Array): Uint8Array {
  const un = new Uint8Array(a.length);
  for (let o = 0; o < a.length; o += 4) {
    const ha = a[o + 1]! > a[o]!, hb = b[o + 1]! > b[o]!;
    if (ha && hb) { un[o] = Math.min(a[o]!, b[o]!); un[o + 1] = Math.max(a[o + 1]!, b[o + 1]!); }
    else if (ha) { un[o] = a[o]!; un[o + 1] = a[o + 1]!; }
    else if (hb) { un[o] = b[o]!; un[o + 1] = b[o + 1]!; }
    const hc = a[o + 3]! > a[o + 2]!, hd = b[o + 3]! > b[o + 2]!;                      // the upper band the same way
    un[o + 2] = 255; un[o + 3] = 0;
    if (hc && hd) { un[o + 2] = Math.min(a[o + 2]!, b[o + 2]!); un[o + 3] = Math.max(a[o + 3]!, b[o + 3]!); }
    else if (hc) { un[o + 2] = a[o + 2]!; un[o + 3] = a[o + 3]!; }
    else if (hd) { un[o + 2] = b[o + 2]!; un[o + 3] = b[o + 3]!; }
  }
  return un;
}


/** The far field: how wide the density is filtered (in pixels of footprint: the filter's width is this times 0.0016 of the range, so 8 is 8 pixels), where the
 *  cloud starts to thin toward the draw distance (share of it: 1 is no thinning, 0 thins from the eye), how much hazier the air in front of the cloud is than the dome's own,
 *  the low cloud's draw distance for an eye at the ground (m) and how far the layers aloft are followed (m). The draw distances grow with the eye's height above the cloud (horizonFor). */
export const FAR_DEFAULT = { lod: 0, fade: 0.7, haze: 1, maxD: 60000, maxA: 150000 };
export type VolumeFar = typeof FAR_DEFAULT;

/** How much the low cloud's draw distance grows per metre the eye is above the top of the cloud volume. A ray that looks down at 14 degrees or more still reaches the volume
 *  within it (it needs 4 times the height). The growth stops at half the world's period for as long as the world is flat and periodic: further out it would repeat itself. */
const HORIZON_PER_HEIGHT = 4;
const LOW_TOP_M = 2800;           // the top of the low range: the shader follows the low cloud to its horizon below this height and only the layers aloft beyond it

/** Steepness (see setSteep): the exponent the picture puts on the density share (1: the extinction as the solver made it; below 1 the edges are sharper and the cloud a little fatter, above 1
 *  softer and thinner). The shipped default is 1.5. */
export const STEEP_DEFAULT = 1.5;

/** The height cue (see setGrad): the share of the beam left at a cloud's base (1 = no cue), the curve (an exponent on the sky's openness above the point: below 1
 *  the cue is spread toward the top), and how far the ambient light follows it. It reads the baked openness, so a thin cloud has little of it and a deep one all of it. The eye assumes light from above; a gradient from lit top to dark base that holds through
 *  the whole structure is how a cloud reads as tall (or, as a thin dark-bottomed sheet, as wide). */
export const GRAD_DEFAULT = { base: 0.35, curve: 0.7, amb: 0.5 };
export type VolumeGrad = typeof GRAD_DEFAULT;

/** Lobe shading (see setShade): `shade` strength of the multi-scale sun-side shading, `tap` the first tap in pixels of footprint, `ratio` between taps, `from` and `full` the range (m) where the shading
 *  starts and where it is complete. */
export const SHADE_DEFAULT = { shade: 1.0, tap: 5, ratio: 2.8, from: 1200, full: 4000 };
export type VolumeShade = typeof SHADE_DEFAULT;

/** The fine structure (see setDetail): `k` how much of the density the erosion can take (0 = none, 1 = all; the cell's mean extinction is kept by the gain table), `from` and `full` the size
 *  of the biggest lobe a level's erosion carries (267 m for the finest rings up to 2133 m for the world level), in pixels of the march, where the erosion starts to come in and where it is complete, `on` the switch. */
export const DETAIL_DEFAULT = { k: 0.5, from: 1.5, full: 4, on: true };
export type VolumeDetail = typeof DETAIL_DEFAULT;
export type VolumeQuality = 0 | 1 | 2;

/** The cross-fade between volumes (see CloudVolume.blendStep) redraws its blend this often, ms. */
const BLEND_STEP_MS = 200;

/** The window blocks (m) the server's origins are counted in. */
const BLOCK_M = 2000;

/** One level: the last message, and the cross-fade from the state that was shown toward the newest. */
interface LevelState {
  k: number;
  msg: CloudWinMessage | null;
  loaded: boolean;
  seq: number;
  recvAt: number; lastArrive: number; gapMs: number;
  vol: Uint8Array; lit: Uint8Array; ext: Uint8Array; rain: Uint8Array;           // what the textures hold for this level (views into the stacked arrays): the blended, displayed state (rain: RGBA, R the rate G the base)
  from: { vol: Uint8Array; lit: Uint8Array } | null; to: { vol: Uint8Array; lit: Uint8Array } | null;       // the cross-fade: where it starts and where it ends (`vol`, `lit` start as a copy of `to`, so only the stretches `runs` that differ are blended)
  runs: { vol?: Uint32Array; lit?: Uint32Array };
  extFrom: Uint8Array | null; extTo: Uint8Array | null;                           // the ranges: the union of both while the two differ
  rainFrom: Uint8Array | null; rainTo: Uint8Array | null;                         // the rain planes: blended like the light
  blend: { t0: number; T: number; last: number; done: boolean };
}

/** What the shader's ground lookup (groundClip.ts GROUND_GLSL) reads before a clipmap is given: no ring in use, so the march sees no ground. */
function noGroundUniforms(): Record<string, THREE.IUniform> {
  const wins: THREE.Vector4[] = [], os: THREE.Vector4[] = [];
  for (let k = 0; k < GROUND_RINGS; k++) { wins.push(new THREE.Vector4(0, 0, 1, 1)); os.push(new THREE.Vector4(1, 1, 0, 0)); }
  return {
    uGH: { value: null }, uGHMax: { value: null }, uGC: { value: null },
    uGWin: { value: wins }, uGO: { value: os }, uGKmin: { value: GROUND_RINGS }, uGTop: { value: -1 },
    uGOrg: { value: new THREE.Vector2() }, uGPar: { value: new THREE.Vector4(NaN, NaN, NaN, NaN) },
  };
}

export class CloudVolume {
  readonly mesh: THREE.Mesh;
  private u: Record<string, THREE.IUniform>;
  private quality: VolumeQuality = 2;
  private erStep = 40;
  private lastIngest = { ms: 0, bytes: 0, level: -1 };
  // The rings' density and light are stacked along the layers (all four are 64 x 64 columns), so one texture holds them; the world level has its own. The ranges are small 2D tables.
  private volR = new Uint8Array(RING_N * RING_N * VTOT);
  private litR = new Uint8Array((RING_N / 2) * (RING_N / 2) * LTOT * 4);
  private extR = new Uint8Array(RING_NB * RING_NB * N_RINGS * 4);
  private volW = new Uint8Array(WORLD_N * WORLD_N * WORLD_NZ);
  private litW = new Uint8Array((WORLD_N / 2) * (WORLD_N / 2) * (WORLD_NZ / 2) * 4);
  private extW = new Uint8Array(WORLD_NB * WORLD_NB * 4);
  private rainR = new Uint8Array(RAIN_N * RAIN_N * N_RINGS * 4);
  private rainW = new Uint8Array(RAIN_NW * RAIN_NW * 4);
  private tex: Record<'volR' | 'litR' | 'extR' | 'volW' | 'litW' | 'extW' | 'rainR' | 'rainW', THREE.Texture>;
  private dirty = new Set<string>();
  private levels: LevelState[] = [];
  // The cross-fade. A new window does not replace the old one in a frame: what is drawn is the old state (as it was shown when the message arrived) blended toward the new one over about
  // one message interval, so a change the model made reads as the cloud changing, not as a swap. The blend is done on the CPU on the bytes the textures hold (one density fetch per sample), a few
  // times a second. The ranges show the union of the two while they differ.
  private blendOn = true;
  private blendMs = 0;                               // cost of the last blend step (the lerps), ms
  // The detail noise's frame: the cloud wind carries it (the noise is looked up at the world point minus this), so the fine structure moves with the cloud. The wind and the clock's rate are the finest level's.
  private noise: [number, number] = [0, 0];
  private noiseT = 0;
  private drift: [number, number] = [0, 0];
  private rate = 1;

  constructor(dome: Sky) {
    const mk3 = (data: Uint8Array, w: number, h: number, d: number, format: THREE.PixelFormat, wrapXY: THREE.Wrapping): THREE.Data3DTexture => {
      const t = new THREE.Data3DTexture(data as unknown as BufferSource, w, h, d);
      t.format = format; t.type = THREE.UnsignedByteType; t.minFilter = t.magFilter = THREE.LinearFilter;
      t.wrapS = t.wrapT = wrapXY; t.wrapR = THREE.ClampToEdgeWrapping; t.unpackAlignment = 1; t.needsUpdate = true;
      return t;
    };
    const mk2 = (data: Uint8Array, w: number, h: number): THREE.DataTexture => {
      const t = new THREE.DataTexture(data as unknown as BufferSource, w, h, THREE.RGBAFormat, THREE.UnsignedByteType);
      t.minFilter = t.magFilter = THREE.NearestFilter; t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping; t.needsUpdate = true;
      return t;
    };
    const mkLin = (data: Uint8Array, w: number, h: number): THREE.DataTexture => {
      const t = new THREE.DataTexture(data as unknown as BufferSource, w, h, THREE.RGBAFormat, THREE.UnsignedByteType);
      t.minFilter = t.magFilter = THREE.LinearFilter; t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping; t.needsUpdate = true;
      return t;
    };
    const nl = RING_N / 2, nlw = WORLD_N / 2;
    this.tex = {
      volR: mk3(this.volR, RING_N, RING_N, VTOT, THREE.RedFormat, THREE.ClampToEdgeWrapping),
      litR: mk3(this.litR, nl, nl, LTOT, THREE.RGBAFormat, THREE.ClampToEdgeWrapping),
      extR: mk2(this.extR, RING_NB, RING_NB * N_RINGS),
      volW: mk3(this.volW, WORLD_N, WORLD_N, WORLD_NZ, THREE.RedFormat, THREE.RepeatWrapping),
      litW: mk3(this.litW, nlw, nlw, WORLD_NZ / 2, THREE.RGBAFormat, THREE.RepeatWrapping),
      extW: mk2(this.extW, WORLD_NB, WORLD_NB),
      rainR: mkLin(this.rainR, RAIN_N, RAIN_N * N_RINGS),
      rainW: mkLin(this.rainW, RAIN_NW, RAIN_NW),
    };
    for (let k = 0; k < N_LEVELS; k++) {
      const world = k === WORLD_LEVEL, nz = world ? WORLD_NZ : RING_NZ[k]!, n = world ? WORLD_N : RING_N;
      const vol = world ? this.volW : this.volR.subarray(VOFF[k]! * n * n, (VOFF[k]! + nz) * n * n);
      const lit = world ? this.litW : this.litR.subarray(LOFF[k]! * nl * nl * 4, (LOFF[k]! + nz / 2) * nl * nl * 4);
      const ext = world ? this.extW : this.extR.subarray(k * RING_NB * RING_NB * 4, (k + 1) * RING_NB * RING_NB * 4);
      const rain = world ? this.rainW : this.rainR.subarray(k * RAIN_N * RAIN_N * 4, (k + 1) * RAIN_N * RAIN_N * 4);
      this.levels.push({ k, msg: null, loaded: false, seq: 0, recvAt: 0, lastArrive: -1, gapMs: 0, vol, lit, ext, rain, from: null, to: null, runs: {}, extFrom: null, extTo: null, rainFrom: null, rainTo: null, blend: { t0: 0, T: 1, last: -1e9, done: true } });
    }
    // the levels' geometry that does not depend on a message: where each is used, how it is stored
    const rg: THREE.Vector4[] = [], rgz: THREE.Vector4[] = [], rgm: THREE.Vector4[] = [], dx: number[] = [];
    for (let k = 0; k < N_LEVELS; k++) {
      if (k === WORLD_LEVEL) {
        rg.push(new THREE.Vector4(0, 0, 512000, 0)); rgz.push(new THREE.Vector4(0, WORLD_NZ, 0, WORLD_NZ / 2)); rgm.push(new THREE.Vector4(1e9, 1, WORLD_NB, 0)); dx.push(2000);
      } else {
        const cell = 125 * (1 << k), size = RING_N * cell;
        rg.push(new THREE.Vector4(0, 0, size, 0)); rgz.push(new THREE.Vector4(VOFF[k]!, RING_NZ[k]!, LOFF[k]!, RING_NZ[k]! / 2));
        rgm.push(new THREE.Vector4(0.25 * size, 4 * cell, RING_NB, k * RING_NB)); dx.push(cell);       // used up to a quarter of the window from the eye (the eye is in the middle half of its window, so that much is always inside it)
      }
    }
    this.u = {
      ...dome.cellUniforms(),
      ...air.uniforms, ...air.mapUniforms, ...planet.uniforms, ...origin.uniforms, uCamRel: { value: new THREE.Vector3() },
      uVolR: { value: this.tex.volR }, uLitR: { value: this.tex.litR }, uExtR: { value: this.tex.extR }, uVolW: { value: this.tex.volW }, uLitW: { value: this.tex.litW }, uExtW: { value: this.tex.extW },
      uRainR: { value: this.tex.rainR }, uRainW: { value: this.tex.rainW }, uRainU: { value: new THREE.Vector3(200, 10000 / 192, 1) },
      uRg: { value: rg }, uRgZ: { value: rgz }, uRgM: { value: rgm }, uDx: { value: dx },
      uZt: { value: new THREE.Vector4(VTOT, LTOT, WORLD_NZ, WORLD_NZ / 2) }, uWorldM: { value: 512000 }, uSpan: { value: 16666.667 }, uExtMax: { value: 0.08 }, uAny: { value: 0 },
      uFar: { value: new THREE.Vector4(FAR_DEFAULT.lod * 0.0016, FAR_DEFAULT.fade, FAR_DEFAULT.haze, 0) }, uFine: { value: 0.6 },
      uMaxD: { value: FAR_DEFAULT.maxD }, uMaxA: { value: FAR_DEFAULT.maxA }, uDebug: { value: 0 },
      uGrad: { value: new THREE.Vector3(GRAD_DEFAULT.base, GRAD_DEFAULT.curve, GRAD_DEFAULT.amb) }, uSteep: { value: STEEP_DEFAULT }, uClip: { value: new THREE.Vector2(-1e9, 1e9) }, uJit: { value: 1 }, uStepMax: { value: 1e9 },
      uShade: { value: SHADE_DEFAULT.shade }, uFx2: { value: new THREE.Vector4(SHADE_DEFAULT.tap, SHADE_DEFAULT.ratio, SHADE_DEFAULT.from, SHADE_DEFAULT.full) }, uTaps: { value: 3 }, uGrowMax: { value: 6 },
      uGndMax: { value: -1e9 },
      ...noGroundUniforms(),
      uPx: { value: 1 }, uMaxSteps: { value: 220 }, uJ: { value: new THREE.Vector2() }, uDepthS: { value: 0 },
      uDet: { value: null }, uDetG: { value: null }, uDetP: { value: new THREE.Vector4(DETAIL_DEFAULT.k, DETAIL_DEFAULT.from, DETAIL_DEFAULT.full, 0) },
      uDetMS: { value: new THREE.Vector2(0.5, 0.25) }, uNoise: { value: new THREE.Vector2() }, uPixRad: { value: 0.0032 },
    };
    const dn = makeDetailNoise();
    const dt3 = new THREE.Data3DTexture(dn.tex as unknown as BufferSource, DET_N, DET_N, DET_N);
    dt3.format = THREE.RedFormat; dt3.type = THREE.UnsignedByteType; dt3.minFilter = dt3.magFilter = THREE.LinearFilter;
    dt3.wrapS = dt3.wrapT = dt3.wrapR = THREE.RepeatWrapping; dt3.unpackAlignment = 1; dt3.needsUpdate = true;
    const dg = new THREE.DataTexture(dn.lut as unknown as BufferSource, 64, 16, THREE.RedFormat, THREE.UnsignedByteType);
    dg.minFilter = dg.magFilter = THREE.LinearFilter; dg.wrapS = dg.wrapT = THREE.ClampToEdgeWrapping; dg.unpackAlignment = 1; dg.needsUpdate = true;
    this.u.uDet!.value = dt3; this.u.uDetG!.value = dg;
    (this.u.uDetMS!.value as THREE.Vector2).set(dn.mean, dn.std);
    // One shader, three ways to draw it (they share the uniforms, and the ones that draw into a target share a compiled program):
    //   * matScreen: straight to the screen, full size, depth-tested against the scene (scale 1: how it was drawn before the small target existed);
    //   * matSmall: into a small target, cleared to nothing, as colour times alpha (so that stretching it back does not darken the edges); the cloud's distance goes to the target's depth attachment;
    //   * matProbe: into the sky probe's cube, where the alpha channel is the transmittance (see beginProbe).
    // matScreen and matProbe write the cloud's depth in the camera that draws them (the scene in front of the cloud hides it, the cloud in front of the scene shows); matSmall writes the camera free form (DEPTH_K).
    const geo = new THREE.PlaneGeometry(2, 2);
    const common = { vertexShader: VERT, fragmentShader: FRAG, uniforms: this.u, side: THREE.DoubleSide, transparent: true, depthWrite: false, fog: false, toneMapped: false };
    this.matScreen = new THREE.ShaderMaterial({ ...common, depthTest: true });
    this.matSmall = new THREE.ShaderMaterial({
      ...common, depthTest: true, depthWrite: true, depthFunc: THREE.AlwaysDepth, blending: THREE.CustomBlending, blendEquation: THREE.AddEquation,
      blendSrc: THREE.SrcAlphaFactor, blendDst: THREE.ZeroFactor, blendSrcAlpha: THREE.OneFactor, blendDstAlpha: THREE.ZeroFactor });
    this.matProbe = new THREE.ShaderMaterial({
      ...common, depthTest: true, blending: THREE.CustomBlending, blendEquation: THREE.AddEquation,
      blendSrc: THREE.SrcAlphaFactor, blendDst: THREE.OneMinusSrcAlphaFactor, blendSrcAlpha: THREE.ZeroFactor, blendDstAlpha: THREE.OneMinusSrcAlphaFactor });
    this.matComp = new THREE.ShaderMaterial({
      vertexShader: COMP_VERT, fragmentShader: COMP_FRAG, uniforms: { uTex: { value: null }, uDepth: { value: null }, uDsz: { value: new THREE.Vector2(2, 2) }, uOff: { value: new THREE.Vector2() }, uLayers: { value: COMP_LAYERS }, uOn: { value: 0 }, uNearFade: { value: 1 }, uHand: handover.hand, uHandP: handover.handP },
      side: THREE.DoubleSide, transparent: true, depthTest: true, depthWrite: false, fog: false, toneMapped: false });
    const rv = (v: THREE.IUniform['value']) => ({ value: v });
    this.matRes = new THREE.ShaderMaterial({
      vertexShader: RES_VERT, fragmentShader: RES_FRAG, blending: THREE.NoBlending, depthTest: false, depthWrite: false, fog: false, toneMapped: false,
      uniforms: { uFresh: rv(null), uHist: rv(null), uFreshD: rv(null), uSize: rv(new THREE.Vector2(2, 2)), uPar: rv(new THREE.Vector2()), uProj: rv(this.tProj), uProjPrev: rv(this.tProjPrev),
        uView: rv(this.tView), uViewPrev: rv(this.tViewPrev), uCamD: rv(this.tCamD), uReset: rv(1), uAge: rv(0), uBoost: rv(0) } });
    const rtri = new THREE.BufferGeometry();
    rtri.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 3, -1, 0, -1, 3, 0]), 3));
    const resMesh = new THREE.Mesh(rtri, this.matRes); resMesh.frustumCulled = false; resMesh.name = 'cloud volume (temporal resolve)';
    this.resScene.add(resMesh);
    // What the renderer adds to its scene: the composite of the small target (or, at scale 1, the march itself). Its child is what the probe's camera sees.
    // (the composite draws its quad COMP_LAYERS times in one call, once per depth slice; at scale 1 the march itself is drawn straight to the screen, once: applyMode)
    this.compGeo = new THREE.InstancedBufferGeometry();
    this.compGeo.index = geo.index; this.compGeo.setAttribute('position', geo.getAttribute('position')); this.compGeo.setAttribute('uv', geo.getAttribute('uv'));
    this.compGeo.instanceCount = COMP_LAYERS;
    this.mesh = new THREE.Mesh(this.compGeo, this.matComp);
    this.mesh.frustumCulled = false; this.mesh.renderOrder = 10;
    const eyeFor = (_r: THREE.WebGLRenderer, _s: THREE.Scene, cam: THREE.Camera) => this.setCamRel(cam);
    this.mesh.onBeforeRender = eyeFor;                                  // each draw counts from the origin the eye of the camera that is drawing (the probe's is not the view's)
    this.probeMesh = new THREE.Mesh(geo, this.matProbe);
    this.probeMesh.frustumCulled = false; this.probeMesh.renderOrder = 10; this.probeMesh.name = 'cloud volume (probe)';
    this.probeMesh.onBeforeRender = eyeFor;
    this.probeMesh.layers.set(SKY_PROBE_LAYER);                    // the probe's camera sees this one (see beginProbe) and the main camera does not
    this.mesh.add(this.probeMesh);
    this.marchMesh = new THREE.Mesh(geo, this.matSmall);
    this.marchMesh.frustumCulled = false; this.marchMesh.name = 'cloud volume (march)';
    this.marchMesh.onBeforeRender = eyeFor;
    this.marchScene.add(this.marchMesh);
    this.applyMode();
    this.mesh.visible = false;
  }

  // ---- The small target ------------------------------------------------------------------------------------------------------------------
  // The march costs per pixel (a hundred texture reads or more for each), and cloud is soft, so by default it is drawn into a target a quarter of the
  // screen's size and stretched back by a cheap, depth-tested quad: the scene in front (trees, rocks, the land) still cuts it out at full resolution.
  // The footprint constants in the shader follow (uPx), so detail finer than a pixel of the small target fades out instead of shimmering.
  private matScreen: THREE.ShaderMaterial; private matSmall: THREE.ShaderMaterial; private matProbe: THREE.ShaderMaterial; private matComp: THREE.ShaderMaterial;
  private compGeo: THREE.InstancedBufferGeometry;
  private layers = COMP_LAYERS;
  /** How many depth slices the composite draws each pixel's cloud as (1 to 8; 1 is one depth at the middle of the cloud's stretch, as it was drawn before the slices; ?cloudlayers=1 to compare). */
  setLayers(n: number) {
    this.layers = Math.min(8, Math.max(1, Math.round(n)));
    this.matComp.uniforms.uLayers.value = this.layers;
    if (this.scale < 0.999) this.compGeo.instanceCount = this.layers;
    return this.layers;
  }
  private probeMesh: THREE.Mesh; private marchMesh: THREE.Mesh; private marchScene = new THREE.Scene();
  private rt: THREE.WebGLRenderTarget | null = null;
  private scale = SMALL_DEFAULT;
  // The temporal march (see RES_FRAG): a quarter-size target the march draws into, two small-size pictures it is resolved into (this frame's and last frame's), and what the resolve needs of the camera.
  private temporal = true;
  private tMarch: THREE.WebGLRenderTarget | null = null; private tHist: THREE.WebGLRenderTarget[] = []; private tCur = 0;
  private tReset = true; private tAge = 0; private tFrame = 0; private tLastMs = 0; private tHave = false;
  private tView = new THREE.Matrix3(); private tViewPrev = new THREE.Matrix3(); private tProj = new THREE.Vector2(); private tProjPrev = new THREE.Vector2();
  private tCamD = new THREE.Vector3();
  private tCam = new THREE.Vector3(); private tCamPrev = new THREE.Vector3();               // where the eye is, and was a frame ago (the resolve reprojects the cloud's points, so the move counts)
  private tSig = new Float32Array(13); private tSigOk = false;
  private matRes: THREE.ShaderMaterial; private resScene = new THREE.Scene(); private resCam = new THREE.Camera();
  private szTmp = new THREE.Vector2(); private clrTmp = new THREE.Color();
  private tapsSet: number | null = null; private erStepSet: number | null = null; private fineSet: number | null = null;

  /** The march's size as a share of the screen's, each way (0.25 to 1; 1 draws it straight to the screen at full size, as before the small target). */
  setScale(s: number) {
    this.scale = Math.min(1, Math.max(0.25, s));
    this.applyMode();
    return this.scale;
  }
  get scaleValue() { return this.scale; }

  private applyMode() {
    const small = this.scale < 0.999;
    this.mesh.material = small ? this.matComp : this.matScreen;
    this.compGeo.instanceCount = small ? this.layers : 1;
    this.mesh.name = small ? 'cloud volume (composite)' : 'cloud volume';
    this.matComp.uniforms.uOn.value = 0;                               // until the target holds a picture of the cloud
    this.u.uPx.value = 1;
    this.tReset = true;
    if (!small && this.rt) { this.disposeTarget(this.rt); this.rt = null; this.matComp.uniforms.uTex.value = null; this.matComp.uniforms.uDepth.value = null; }
    if (!small) this.freeTemporal();
  }

  /** A target for the march: colour times alpha in half floats, and the cloud's distance (DEPTH_K) in a float depth attachment, which the composite and the temporal resolve read. */
  private marchTarget(w: number, h: number): THREE.WebGLRenderTarget {
    const dt = new THREE.DepthTexture(w, h, THREE.FloatType);
    dt.minFilter = dt.magFilter = THREE.NearestFilter; dt.generateMipmaps = false;
    return new THREE.WebGLRenderTarget(w, h, { type: THREE.HalfFloatType, depthBuffer: true, stencilBuffer: false, depthTexture: dt, generateMipmaps: false, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter });
  }
  private disposeTarget(t: THREE.WebGLRenderTarget | null) { if (!t) return; t.depthTexture?.dispose(); t.dispose(); }
  /** The march's own draw into `target`: cleared to nothing (no colour, no cloud: depth 1), the depth written as the camera free distance. */
  private marchInto(renderer: THREE.WebGLRenderer, target: THREE.WebGLRenderTarget, camera: THREE.Camera) {
    const ey = this.eyeTmp.setFromMatrixPosition(camera.matrixWorld).y;
    if (Math.abs(ey - this.eyeY) > 1 || planet.version !== this.planetVer) { this.eyeY = ey; this.applyHorizon(); }               // the draw distances follow the eye's height
    renderer.setRenderTarget(target);
    renderer.autoClear = false;
    renderer.setClearColor(0x000000, 0);
    renderer.clear(true, true, false);
    this.u.uDepthS.value = 1;
    try { renderer.render(this.marchScene, camera); } finally { this.u.uDepthS.value = 0; }
  }

  /** The temporal march (a quarter of the small target's pixels marched a frame, the rest carried from the last frames) on or off. On by default; off, the whole small target is marched every frame. */
  setTemporal(on: boolean) {
    this.temporal = on; this.tReset = true; this.matComp.uniforms.uOn.value = 0;
    if (on) { this.disposeTarget(this.rt); this.rt = null; } else this.freeTemporal();
    return on;
  }
  get temporalOn() { return this.temporal; }
  private freeTemporal() { this.disposeTarget(this.tMarch); this.tMarch = null; for (const r of this.tHist) r.dispose(); this.tHist = []; }
  /** Something the picture depends on has changed other than the camera: the next frame takes the fresh march instead of the old picture. */
  private touch() { this.tReset = true; }

  /** Draw the march into the small target. Call once a frame, after the camera has its final pose and before the main render. Does nothing at scale 1 or while the volume is off. */
  renderView(renderer: THREE.WebGLRenderer, camera: THREE.Camera) {
    if (this.scale >= 0.999 || !this.active || !this.mesh.visible || this.mesh.layers.mask === 0) return;
    const size = renderer.getDrawingBufferSize(this.szTmp);
    if (this.temporal) { this.renderTemporal(renderer, camera, size); return; }
    const w = Math.max(2, Math.round(size.x * this.scale)), h = Math.max(2, Math.round(size.y * this.scale));
    if (!this.rt || this.rt.width !== w || this.rt.height !== h) {
      this.disposeTarget(this.rt);
      this.rt = this.marchTarget(w, h);
      this.matComp.uniforms.uTex.value = this.rt.texture; this.matComp.uniforms.uDepth.value = this.rt.depthTexture;
      (this.matComp.uniforms.uDsz.value as THREE.Vector2).set(w, h);
    }
    (this.matComp.uniforms.uOff.value as THREE.Vector2).set(0, 0);          // (every pixel of this target is marched through its own middle)
    this.u.uPx.value = size.x / w;
    this.u.uPixRad.value = 2 / ((camera as THREE.PerspectiveCamera).projectionMatrix.elements[5]! * h);
    const prevTarget = renderer.getRenderTarget(), prevAuto = renderer.autoClear, prevAlpha = renderer.getClearAlpha();
    renderer.getClearColor(this.clrTmp);
    try {
      this.marchInto(renderer, this.rt, camera);
      this.matComp.uniforms.uOn.value = 1;
    } finally {
      renderer.setClearColor(this.clrTmp, prevAlpha);
      renderer.autoClear = prevAuto;
      renderer.setRenderTarget(prevTarget);
    }
  }

  private renderTemporal(renderer: THREE.WebGLRenderer, camera: THREE.Camera, size: THREE.Vector2) {
    const w = 2 * Math.max(2, Math.round(size.x * this.scale / 2)), h = 2 * Math.max(2, Math.round(size.y * this.scale / 2));   // even, so the quarter target is exactly half each way
    const mk = (ww: number, hh: number) => new THREE.WebGLRenderTarget(ww, hh, { type: THREE.HalfFloatType, depthBuffer: false, stencilBuffer: false, generateMipmaps: false, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter });
    if (!this.tMarch || this.tMarch.width !== w / 2 || this.tMarch.height !== h / 2) {
      this.freeTemporal();
      this.tMarch = this.marchTarget(w / 2, h / 2); this.tHist = [mk(w, h), mk(w, h)]; this.tReset = true;
      (this.matComp.uniforms.uDsz.value as THREE.Vector2).set(w / 2, h / 2);
    }
    const now = performance.now();
    if (now - this.tLastMs > 400) this.tReset = true;                                         // the volume was not drawn for a while: what the old picture holds is not the sky now
    this.tLastMs = now;
    camera.updateMatrixWorld();
    this.tView.setFromMatrix4(camera.matrixWorldInverse);
    this.tCam.setFromMatrixPosition(camera.matrixWorld);
    this.tCamD.subVectors(this.tCam, this.tCamPrev);                                        // (in doubles: the two positions are large numbers far from the origin)
    if (this.tHave && this.tCam.distanceToSquared(this.tCamPrev) > 500 * 500) this.tReset = true;     // the eye jumped (a teleport, a respawn): the old picture is of another place
    const pe = (camera as THREE.PerspectiveCamera).projectionMatrix.elements;
    this.tProj.set(pe[0], pe[5]);
    // a change of light (the sun's colour, the exposure) must not show as a checker of new and old pixels: it takes the picture over at once, in proportion to how much it moved
    const U = this.u, sig = this.tSig, a3 = (k: string, o: number) => { const v = U[k]?.value as THREE.Vector3 | undefined; sig[o] = v?.x ?? 0; sig[o + 1] = v?.y ?? 0; sig[o + 2] = v?.z ?? 0; };
    const keep = this.tSigOk ? Float32Array.from(sig) : null;
    a3('uAmb', 0); a3('uSrc', 3); a3('uLDir', 6); sig[9] = (U.uGain?.value as number) ?? 0; sig[10] = (U.uCgain?.value as THREE.Vector2)?.x ?? 0; sig[11] = (U.uCgain?.value as THREE.Vector2)?.y ?? 0; sig[12] = (U.uNight?.value as number) ?? 0;
    let boost = 0;
    if (keep) { let d = 0, n = 1e-6; for (let i = 0; i < 13; i++) { d += Math.abs(sig[i] - keep[i]); n += Math.abs(keep[i]); } boost = Math.min(1, d / n / 0.02); }
    this.tSigOk = true;
    const ph = this.tFrame++ & 3, ox = [0, 1, 1, 0][ph], oy = [0, 1, 0, 1][ph];
    U.uPx.value = size.x / w;
    U.uPixRad.value = 2 / (pe[5]! * h);                                                      // the angle one pixel of the small target covers (rad)
    (U.uJ.value as THREE.Vector2).set((ox - 0.5) * 2 / w, (oy - 0.5) * 2 / h);
    const prevTarget = renderer.getRenderTarget(), prevAuto = renderer.autoClear, prevAlpha = renderer.getClearAlpha();
    renderer.getClearColor(this.clrTmp);
    try {
      this.marchInto(renderer, this.tMarch, camera);
      (U.uJ.value as THREE.Vector2).set(0, 0);
      const prev = this.tHist[this.tCur], cur = this.tHist[1 - this.tCur], R = this.matRes.uniforms;
      R.uFresh.value = this.tMarch.texture; R.uFreshD.value = this.tMarch.depthTexture; R.uHist.value = prev.texture; R.uSize.value.set(w, h); R.uPar.value.set(ox, oy);
      R.uReset.value = (this.tReset || !this.tHave) ? 1 : 0; R.uAge.value = this.tAge; R.uBoost.value = boost;
      renderer.setRenderTarget(cur);
      renderer.render(this.resScene, this.resCam);
      this.tCur = 1 - this.tCur;
      this.matComp.uniforms.uTex.value = cur.texture; this.matComp.uniforms.uDepth.value = this.tMarch.depthTexture;
      (this.matComp.uniforms.uOff.value as THREE.Vector2).set((ox - 0.5) / w, (oy - 0.5) / h);       // the march texel (i, j) was marched through the pixel (2i + ox, 2j + oy): the composite reads it there (as the resolve does)
      this.matComp.uniforms.uOn.value = 1;
    } finally {
      (U.uJ.value as THREE.Vector2).set(0, 0);
      renderer.setClearColor(this.clrTmp, prevAlpha);
      renderer.autoClear = prevAuto;
      renderer.setRenderTarget(prevTarget);
    }
    this.tViewPrev.copy(this.tView); this.tProjPrev.copy(this.tProj); this.tCamPrev.copy(this.tCam);
    this.tAge = this.tReset || !this.tHave ? 0 : this.tAge + 1;
    this.tReset = false; this.tHave = true;
  }


  private on = true;
  /** True once a window has been uploaded and is drawn (the dome's own low layer should then stand down). */
  /** How opaque the cloud composite is (1 near the ground, falling to 0 as the eye leaves the planet: bodies.ts nearFade). */
  setNearFade(v: number) { this.matComp.uniforms.uNearFade.value = Math.min(1, Math.max(0, v)); }

  get active(): boolean { return this.on && this.levels.some(l => l.loaded); }
  /** The windows carry the layers aloft (the server paints the mid and upper layers in them): the dome's flat high layer would double them. */
  get hasAloft(): boolean { return this.active; }
  get enabled(): boolean { return this.on; }
  /** Draw it or not (a message that arrives while it is off is not decoded; the next one after it is switched on is). */
  setEnabled(on: boolean) { this.on = on; this.touch(); this.mesh.visible = this.active; this.matComp.uniforms.uOn.value = 0; }

  /** 0: the plain volume at 1 cell steps (phones); 1 and 2: plus the shading taps, 40 m steps and the fine detail. Also puts the cost knobs below back to what the tier says. */
  setQuality(q: VolumeQuality) {
    this.quality = q; this.touch();
    this.tapsSet = null; this.erStepSet = null; this.fineSet = null; this.u.uMaxSteps.value = 220;
    this.applyQuality();
  }

  /** Cost knobs, for the panel, the profiler's variants and experiments; setQuality puts them back. `taps`: shading taps per lit sample (0 to 3; null = the tier's),
   *  `erStep`: the longest step inside cloud, metres (40 by default; tiers 1 and 2), `fine`: a step as a share of a cell (0.6 by default, 1 at tier 0; null = the tier's),
   *  `maxSteps`: the step budget of a ray (220 = the loop's own limit). */
  setCost(c: { taps?: number | null; erStep?: number | null; fine?: number | null; maxSteps?: number }) {
    this.touch();
    if (c.taps !== undefined) this.tapsSet = c.taps;
    if (c.erStep !== undefined) this.erStepSet = c.erStep;
    if (c.fine !== undefined) this.fineSet = c.fine;
    if (c.maxSteps !== undefined) this.u.uMaxSteps.value = Math.min(220, Math.max(1, c.maxSteps));
    this.applyQuality();
    return { taps: this.u.uTaps.value, erStep: this.u.uStepMax.value, fine: this.u.uFine.value, maxSteps: this.u.uMaxSteps.value };
  }

  private applyQuality() {
    const q = this.quality;
    this.u.uFine.value = this.fineSet ?? (q === 0 ? 1.0 : 0.6);
    const capOn = q >= 1 && this.active;
    this.u.uStepMax.value = capOn ? (this.erStepSet ?? this.erStep) : 1e9;
    this.u.uTaps.value = this.tapsSet ?? (q >= 2 ? 3 : q === 1 ? 2 : 0);   // the multi-scale shading's taps per lit sample (see setShade)
    this.u.uDetP.value.w = (q >= 1 && this.detail.on) ? 1 : 0;
  }

  private detail: VolumeDetail = { ...DETAIL_DEFAULT };
  /** The fine structure below the cell (see DETAIL_DEFAULT) on or off, or retuned. It is drawn from quality 1 up, near the camera only (where a cell is several pixels wide), and keeps each
   *  cell's mean extinction; the noise's frame is carried by the cloud wind, so it moves with the cloud. Returns what is set. */
  setDetail(d: Partial<VolumeDetail>) {
    this.detail = { ...this.detail, ...d };
    const v = this.u.uDetP!.value as THREE.Vector4;
    v.x = Math.min(1, Math.max(0, this.detail.k)); v.y = this.detail.from; v.z = Math.max(this.detail.full, this.detail.from + 0.01);
    this.touch(); this.applyQuality();
    return { ...this.detail };
  }

  /** A "cloud_win" message: decode, then blend toward it (or, the first time or after a break, show it). Messages of one level that arrive while one is still decoding replace it. */
  async ingest(msg: CloudWinMessage): Promise<void> {
    const k = Math.trunc(msg.level);
    if (!(k >= 0 && k < N_LEVELS)) throw new Error(`cloud window: level ${msg.level}`);
    const world = k === WORLD_LEVEL, n = world ? WORLD_N : RING_N, nz = world ? WORLD_NZ : RING_NZ[k]!;
    if (msg.vol.n !== n || msg.vol.nz !== nz || msg.lit.n !== n / 2 || msg.lit.nz !== nz / 2 || msg.ext.nb !== n / EXT_BLK || msg.dec.vol.length !== n * n * nz)
      throw new Error(`cloud window: level ${k} is ${msg.vol.n} x ${msg.vol.nz}, expected ${n} x ${nz}`);
    const L = this.levels[k]!, my = ++L.seq, t0 = performance.now();
    const dec = msg.dec, again = msg.decUsed === true;
    msg.decUsed = true;
    const vol = again ? dec.vol.slice() : dec.vol;
    const extRaw = await inflate(b64bytes(msg.ext.d));
    const rainRaw = msg.rain && msg.rain.n === n / 2 ? await inflate(b64bytes(msg.rain.d)) : null;
    if (my !== L.seq) return;
    const nl = n / 2, nlz = nz / 2, cells = nl * nl * nlz;
    // light: two planes (direct, sky) of levels, to bytes 0..255 in RGBA, the roughness in B
    const rgba = litToRgba(dec.lit, cells, 255 / msg.lit.levels, dec.win.length === cells ? dec.win : null);
    const nb = n / EXT_BLK, eb = msg.ext.bands ?? 1, ex = new Uint8Array(nb * nb * 4);       // two height ranges per block (below and above 2.8 km): R G and B A; an older server sends one, and the second is empty
    for (let i = 0; i < nb * nb; i++) {
      ex[4 * i] = extRaw[2 * eb * i]!; ex[4 * i + 1] = extRaw[2 * eb * i + 1]!;
      if (eb > 1) { ex[4 * i + 2] = extRaw[2 * eb * i + 2]!; ex[4 * i + 3] = extRaw[2 * eb * i + 3]!; } else { ex[4 * i + 2] = 255; ex[4 * i + 3] = 0; }
    }
    const nr = n / 2, rain = new Uint8Array(nr * nr * 4);                                      // R the rate, G the base, A 255; dry when the server sent no rain
    if (rainRaw !== null && rainRaw.length >= 2 * nr * nr) for (let i = 0; i < nr * nr; i++) { rain[4 * i] = rainRaw[i]!; rain[4 * i + 1] = rainRaw[nr * nr + i]!; rain[4 * i + 3] = 255; }
    await yieldToFrame();
    if (my !== L.seq) return;
    // from here to the end nothing waits: the state changes in one piece
    this.commit(L, msg, vol, rgba, ex, rain, t0);
  }

  private commit(L: LevelState, msg: CloudWinMessage, vol: Uint8Array, rgba: Uint8Array, ex: Uint8Array, rain: Uint8Array, t0: number) {
    const now = performance.now(), prev = L.msg, k = L.k, world = k === WORLD_LEVEL;
    const n = world ? WORLD_N : RING_N, nz = world ? WORLD_NZ : RING_NZ[k]!, nl = n / 2, nlz = nz / 2, nb = n / EXT_BLK;
    // Blend or show? Blend when the new window is the same kind of thing as the one shown (the clock has not gone back, the world is the same); otherwise (first message, a restart) it is shown at once.
    const blend = this.blendOn && L.loaded && prev !== null && msg.t >= prev.t && prev.world_m === msg.world_m;
    if (L.lastArrive >= 0) { const g = Math.min(120000, Math.max(1500, t0 - L.lastArrive)); L.gapMs = L.gapMs > 0 ? 0.7 * L.gapMs + 0.3 * g : g; }
    L.lastArrive = t0;
    if (blend) {
      // The window may have moved (the eye left the middle of the old one): the old state is shifted by the whole columns, so that the fade is of the same cloud at the same place.
      let dcx = 0, dcy = 0;
      if (!world) {
        const B = Math.max(1, Math.round(BLOCK_M / msg.vol.dx)), wb = Math.max(1, Math.round(msg.world_m / BLOCK_M));
        dcx = wrapBlocks(msg.ox - prev!.ox, wb) * B; dcy = -wrapBlocks(msg.oy - prev!.oy, wb) * B;           // new[r][c] = old[r + dcy][c + dcx]: east is columns, south is rows (the window's origin is its south-west block)
      }
      const sv = L.vol.slice(), sl = L.lit.slice(), se = L.ext.slice(), sr = L.rain.slice();
      const moved = dcx !== 0 || dcy !== 0;
      const fromVol = moved ? shiftWin(sv, vol, n, nz, 1, dcx, dcy) : sv;
      const fromLit = moved ? shiftWin(sl, rgba, nl, nlz, 4, Math.round(dcx / 2), Math.round(dcy / 2)) : sl;
      const fromExt = moved ? shiftWin(se, ex, nb, 1, 4, Math.round(dcx / EXT_BLK), Math.round(dcy / EXT_BLK)) : se;
      const fromRain = moved ? shiftWin(sr, rain, nl, 1, 4, Math.round(dcx / 2), Math.round(dcy / 2)) : sr;
      // Where the two states are equal the blend is that value, so the display starts as a copy of the new state and each step only works on the stretches that differ.
      L.vol.set(vol); L.lit.set(rgba);
      L.runs = { vol: diffRuns(fromVol, vol, vol.length & ~3) ?? undefined, lit: diffRuns(fromLit, rgba, rgba.length & ~3) ?? undefined };
      L.from = { vol: fromVol, lit: fromLit }; L.to = { vol, lit: rgba };
      L.extFrom = fromExt; L.extTo = ex; L.rainFrom = fromRain; L.rainTo = rain;
      L.blend = { t0: now, T: L.gapMs > 0 ? L.gapMs : 6000, last: -1e9, done: false };
      L.msg = msg; L.recvAt = t0;
      this.blendStep(L, now, true);                                                       // the textures show the old state again (k = 0), now in the new window
    } else {
      L.vol.set(vol); L.lit.set(rgba); L.ext.set(ex); L.rain.set(rain);
      L.from = L.to = null; L.runs = {}; L.extFrom = L.extTo = null; L.rainFrom = L.rainTo = null;
      L.blend = { t0: now, T: 1, last: -1e9, done: true };
      L.msg = msg; L.recvAt = t0;
      this.markDirty(k);
      this.touch();                                                                       // a window shown at once, not blended in: the old picture is of another sky
    }
    L.loaded = true;
    // where this level is, and what it is made of
    const U = this.u;
    (U.uRg!.value as THREE.Vector4[])[k]!.set(0, 0, world ? msg.world_m : n * msg.vol.dx, 1);
    this.rgSrc[k] = { x: msg.vol.x0, z: msg.vol.z0m, w: msg.world_m };
    this.applyRg(k);
    U.uWorldM!.value = msg.world_m; U.uSpan!.value = msg.ext.zmax; U.uExtMax!.value = msg.vol.ext_max; U.uAny!.value = 1;
    if (msg.rain) (U.uRainU!.value as THREE.Vector3).set(msg.rain.rmax, msg.rain.dz, (U.uRainU!.value as THREE.Vector3).z);
    this.pickWind();
    this.lastIngest = { ms: performance.now() - t0, bytes: vol.length + rgba.length + ex.length + rain.length, level: k };
    this.flush();
    this.mesh.visible = this.active;
    this.applyQuality();
  }

  /** The cloud wind that carries the detail noise: the finest level's. */
  private pickWind() {
    for (const L of this.levels) {
      if (L.msg) { this.drift = [L.msg.drift[0], L.msg.drift[1]]; this.rate = L.msg.rate; return; }
    }
  }

  private markDirty(k: number) {
    const w = k === WORLD_LEVEL ? 'W' : 'R';
    this.dirty.add('vol' + w); this.dirty.add('lit' + w); this.dirty.add('ext' + w); this.dirty.add('rain' + w);
  }

  /** Tell the GPU what changed (once, however many levels did). */
  private flush() {
    if (this.dirty.size === 0) return;
    for (const name of this.dirty) this.tex[name as keyof typeof this.tex].needsUpdate = true;
    this.dirty.clear();
  }

  /** The cross-fade of one level: redo what its textures show as the old state blended toward the new by how far the fade has come (linear in time: a chain of messages gives a piecewise linear
   *  history, no stops at the joins), in the byte, which is the square root of the extinction, so a cloud grows in as the square of the blend and the water of two states is not added up.
   *  At 1 the display holds the new bytes. Returns true if it changed anything. */
  private blendStep(L: LevelState, now: number, force = false): boolean {
    const b = L.blend;
    if (b.done || !L.from || !L.to) return false;
    const a = Math.min(1, Math.max(0, (now - b.t0) / b.T));
    if (!force && a < 1 && now - b.last < BLEND_STEP_MS) return false;
    b.last = now;
    const t0 = performance.now(), k = Math.round(a * 256);
    for (const name of ['vol', 'lit'] as const) {
      const runs = L.runs[name];
      if (runs) lerp8Runs(L.from[name], L.to[name], L[name], runs, k);                  // only where the two states differ
      else lerp8(L.from[name], L.to[name], L[name], k);
    }
    L.ext.set(a >= 1 ? L.extTo! : unionExt(L.extFrom!, L.extTo!));
    if (L.rainFrom && L.rainTo) lerp8(L.rainFrom, L.rainTo, L.rain, k);                    // (a few KB: the whole array)
    if (a >= 1) { b.done = true; L.from = L.to = null; L.runs = {}; L.extFrom = L.extTo = null; L.rainFrom = L.rainTo = null; }
    this.markDirty(L.k);
    this.blendMs = performance.now() - t0;
    return true;
  }

  /** The windows as the light reads them (skyLight.ts): the bytes the density textures hold now (the blended, displayed state) and where each window is, so the beam and the dome are marched
   *  through the cloud that is drawn. One object, refilled in place (nothing is allocated). null with no window or the volume off. Reads, never writes. */
  lightView(_nowMs?: number): VolView | null {
    if (!this.active) return null;
    const v = this.lightV;
    let m = 0;
    v.layers.length = 0;
    for (const L of this.levels) {
      const msg = L.msg;
      if (!L.loaded || !msg) continue;
      const k = L.k, world = k === WORLD_LEVEL, n = world ? WORLD_N : RING_N, nz = world ? WORLD_NZ : RING_NZ[k]!;
      const lay = this.layerViews[m++]!;
      v.layers.push(lay);
      lay.vol = world ? this.volW : this.volR; lay.off = world ? 0 : VOFF[k]! * n * n; lay.n = n; lay.nz = nz;
      lay.dx = msg.vol.dx; lay.dz = msg.vol.dz; lay.cx = msg.vol.x0; lay.cz = msg.vol.z0m; lay.size = world ? msg.world_m : n * msg.vol.dx;
      lay.reach = world ? 1e12 : 0.25 * lay.size; lay.wrap = world;
    }
    const any = this.levels.find(l => l.msg)!.msg!;
    v.worldM = any.world_m; v.span = any.ext.zmax; v.extMax = any.vol.ext_max; v.steep = this.steep;
    return v;
  }
  private layerViews: RingLayer[] = Array.from({ length: N_LEVELS }, () => ({ vol: new Uint8Array(0), off: 0, n: 0, nz: 0, dx: 1, dz: 1, cx: 0, cz: 0, size: 1, reach: 0, wrap: false }));
  private lightV: VolView = { layers: [], worldM: 512000, span: 16666.667, extMax: 0.08, steep: 1 };

  /** Once a frame: step the cross-fades, and carry the detail noise's frame with the cloud wind. */
  update(nowMs = performance.now()) {
    if (!this.levels.some(l => l.msg)) return;
    for (const L of this.levels) this.blendStep(L, nowMs);
    this.flush();
    this.syncGround();
    const dt = this.noiseT > 0 ? Math.min(0.25, Math.max(0, (nowMs - this.noiseT) / 1000)) : 0;
    this.noiseT = nowMs;
    const dm = dt * this.rate;
    this.noise[0] = (((this.noise[0] + this.drift[0] * dm) % NOISE_WRAP) + NOISE_WRAP) % NOISE_WRAP;
    this.noise[1] = (((this.noise[1] + this.drift[1] * dm) % NOISE_WRAP) + NOISE_WRAP) % NOISE_WRAP;
    this.rebase();
  }

  // ---- Counting from the floating origin (origin.ts) ------------------------------------------------------------------------------------
  // The shader works in positions relative to O. What it takes from the world's own numbers (the windows' corners, the noise frame) is handed to it as that number minus O, taken into the
  // period it is used with (the world's edge for a window, NOISE_WRAP for the noise), all in doubles here, so what the shader subtracts are two small numbers and not two of 250 km.
  private rgSrc: { x: number; z: number; w: number }[] = Array.from({ length: N_LEVELS }, () => ({ x: 0, z: 0, w: 512000 }));
  private originVer = -1;
  private applyRg(k: number) {
    const s = this.rgSrc[k]!, v = (this.u.uRg!.value as THREE.Vector4[])[k]!;
    v.x = s.x - origin.org.x - s.w * Math.floor((s.x - origin.org.x) / s.w + 0.5);       // into [-w/2, w/2): the window is near the eye, so near O
    v.y = s.z - origin.org.y - s.w * Math.floor((s.z - origin.org.y) / s.w + 0.5);
  }
  /** The noise's frame and the windows' corners counted from O: every frame (the noise drifts), the corners when O moves. */
  private rebase() {
    if (this.originVer !== origin.version) { this.originVer = origin.version; for (let k = 0; k < N_LEVELS; k++) this.applyRg(k); }
    const nx = (((this.noise[0] - origin.org.x) % NOISE_WRAP) + NOISE_WRAP) % NOISE_WRAP, nz = (((this.noise[1] - origin.org.y) % NOISE_WRAP) + NOISE_WRAP) % NOISE_WRAP;
    (this.u.uNoise!.value as THREE.Vector2).set(nx, nz);
  }
  // ---- The ground (groundClip.ts) ---------------------------------------------------------------------------------------------------------
  // The march counts from the sea (slabRange), so it does not know where the land is: a ray into a slope went on through it, and the cloud it met inside the rock, drawn against the terrain's own
  // depth, showed as stairs along the rows of equal distance. With the ground clipmap given, each ray ends where it meets the surface (the same heights the terrain is drawn from, read in the shader).
  private ground: GroundClip | null = null;
  private groundOn = true;
  /** Give the march the ground clipmap the terrain is drawn from (shared, not copied: the shader reads its textures and its per ring offsets). */
  setGround(clip: GroundClip | null) {
    this.ground = clip;
    const g = clip?.uniforms, u = this.u;
    if (g) { u.uGH = g.uGH; u.uGHMax = g.uGHMax; u.uGC = g.uGC; u.uGWin = g.uGWin; u.uGO = g.uGO; u.uGKmin = g.uGKmin; u.uGTop = g.uGTop; u.uGOrg = g.uGOrg; u.uGPar = g.uGPar; }
    else Object.assign(u, noGroundUniforms());
    this.syncGround();
    this.touch();
  }
  /** Switch the ground clip off and on (?groundclip=off, to compare). */
  setGroundClip(on: boolean) { this.groundOn = on; this.syncGround(); this.touch(); return on; }

  /** The uniforms the cloud shadow pass (cloudShadow.ts) shares with the march: the density textures and the levels' windows, the unit of the extinction and the exponent the picture puts on it.
   *  The objects are the march's own, so a window that arrives or moves reaches the shadows with no call. */
  shadowUniforms(): Record<string, THREE.IUniform> {
    const u = this.u, out: Record<string, THREE.IUniform> = {};
    for (const n of ['uVolR', 'uLitR', 'uVolW', 'uLitW', 'uRg', 'uRgZ', 'uRgM', 'uDx', 'uZt', 'uWorldM', 'uSpan', 'uExtMax', 'uAny', 'uSteep']) out[n] = u[n]!;
    return out;
  }
  /** Whether any level of the volume is loaded and drawn (the shadows have nothing to cast before). */
  get shadowReady(): boolean { return this.active; }
  /** Once a frame, after the floating origin has moved (the clipmap counts its rings from it): bring the clipmap up to date (the terrain does the same, later: the second call finds nothing to do) and tell
   *  the shader how high the ground gets, under which it has nothing to look up. */
  private syncGround() {
    const g = this.ground;
    let top = -1e9;
    if (g && this.groundOn) {
      g.sync(origin.org.x, origin.org.y);
      if (g.ready) for (let k = g.kmin; k <= g.top; k++) { const r = g.ring(k); if (r && r.hi > top) top = r.hi; }
    }
    this.u.uGndMax!.value = top;
  }

  /** The eye of the camera that is about to draw, counted from O. */
  private setCamRel(cam: THREE.Camera) {
    const e = cam.matrixWorld.elements;
    origin.relTo(e[12]!, e[13]!, e[14]!, this.u.uCamRel!.value as THREE.Vector3);
  }

  private probeSave: { taps: number; fine: number; far: number; px: number; det: number } | null = null;

  /** The probe (sky.ts: a small cube of the sky as seen from the eye, whose alpha is how much light the cloud in each direction lets through) draws the volume too,
   *  so the sun's disc, the moon and the stars are hidden by the cloud that is drawn in front of them. For that draw the march is cheap (no shading taps, a wide
   *  filter, so a few steps) and the alpha channel is the transmittance: the blend of the probe's own material (matProbe) multiplies what is there by (1 - alpha)
   *  instead of mixing alpha in. The probe sees `probeMesh` (its layer), never the composite or the march of the small target. */
  beginProbe() {
    const u = this.u;
    this.probeSave = { taps: u.uTaps.value, fine: u.uFine.value, far: u.uFar.value.x, px: u.uPx.value, det: u.uDetP.value.w };
    u.uTaps.value = 0; u.uFine.value = Math.max(u.uFine.value, 1.5); u.uFar.value.x = 16 * 0.0016; u.uPx.value = 1; u.uDetP.value.w = 0;
  }
  endProbe() {
    const u = this.u, s = this.probeSave;
    if (s) { u.uTaps.value = s.taps; u.uFine.value = s.fine; u.uFar.value.x = s.far; u.uPx.value = s.px; u.uDetP.value.w = s.det; this.probeSave = null; }
  }


  private far: VolumeFar = { ...FAR_DEFAULT };

  /** The far field (see FAR_DEFAULT): `lod` 0 turns the filtering off, `fade` 1 the thinning, `haze` scales the distance the air in front of the cloud is read over (1: as it is); `maxD` is how far a ray is followed
   *  (the cloud's horizon). Takes any of the four. */
  setFar(f: Partial<VolumeFar>) {
    this.far = { ...this.far, ...f }; this.touch();
    this.u.uFar.value.set(this.far.lod * 0.0016, this.far.fade, this.far.haze, 0);
    this.applyHorizon();
    return this.far;
  }

  /** The draw distances for an eye `eyeY` metres up (the volume's own frame: its bottom is 0): the low cloud's own distance (`maxD`) plus 4 times the eye's height above the volume's top,
   *  at most half the world's period (never less than `maxD` itself), and the layers aloft's distance, never shorter than that. Nothing here knows which way is up for the cloud: it is the
   *  distance to the volume's near face. On the planet (planet.ts) neither goes past what the eye can see at all: the cloud at the top of the low range (2.8 km) and at the top of the volume is
   *  hidden by the curve beyond the sum of the two tangent distances, so the march does not follow rays out to where they have left the shell. */
  horizonFor(eyeY: number): { low: number; aloft: number } {
    const f = this.far, span = this.u.uSpan.value as number, world = this.u.uWorldM.value as number;
    const above = Math.max(0, eyeY - span);
    // The flat, periodic world is followed no further than half its period, and no further than 4 times the eye's height above it, as long as nothing else shows what is beyond. With the world
    // painter's picture available (handover.ts) the volume is followed out to where the shell has taken over (`reach`) once that is complete, and the composite fades it out beyond the patch's
    // edge, so the box is never seen twice. (While the shell is only partly there the far cloud is still marched: it is faded, not gone.)
    const old = Math.min(Math.max(f.maxD, 0.5 * world), f.maxD + HORIZON_PER_HEIGHT * above);
    const done = handover.avail >= 0.999, reach = Math.max(handover.reach, 0);
    let low = done ? Math.min(old, reach) : old;
    let aloft = Math.max(low, done ? Math.min(f.maxA, reach) : f.maxA);
    const R = planet.radius;
    if (R > 0) {
      low = Math.min(low, 1.02 * visibleDistance(eyeY, LOW_TOP_M, R));
      aloft = Math.min(aloft, 1.02 * visibleDistance(eyeY, span, R));
      aloft = Math.max(aloft, low);
    }
    return { low, aloft };
  }
  private eyeY = 0; private eyeTmp = new THREE.Vector3(); private planetVer = -1;
  private applyHorizon() {
    const h = this.horizonFor(this.eyeY);
    this.u.uMaxD.value = h.low; this.u.uMaxA.value = h.aloft;
    this.planetVer = planet.version;
  }

  /** Steepness: the exponent on the density share (1: the extinction as the solver made it; below 1 the edges are sharper and the cloud a little fatter). The light follows what is drawn (lightView). */
  setSteep(s: number) { this.steep = s; this.touch(); this.u.uSteep.value = s; return s; }
  private steep = STEEP_DEFAULT;

  private shade: VolumeShade = { ...SHADE_DEFAULT };
  /** Lobe shading: a lit side and a shaded side on every lobe of the volume, from taps toward the light that reach as far as the pixels do. Takes any of the five (see SHADE_DEFAULT). */
  setShade(f: Partial<VolumeShade>) {
    this.shade = { ...this.shade, ...f }; this.touch();
    const F = this.shade;
    this.u.uShade.value = F.shade;
    (this.u.uFx2.value as THREE.Vector4).set(F.tap, F.ratio, F.from, F.full);
    return F;
  }


  /** Draw only the cloud between two heights (m above the sea): to look at one deck, or the towers, on its own. Defaults are no clip. */
  setClip(zmin = -1e9, zmax = 1e9) { this.touch(); (this.u.uClip.value as THREE.Vector2).set(zmin, zmax); return [zmin, zmax]; }

  private grad: VolumeGrad = { ...GRAD_DEFAULT };

  /** The height cue: `base` 1 turns it off, `curve` the exponent on the openness, `amb` how far the ambient light follows. Takes any of the three. */
  setGrad(g: Partial<VolumeGrad>) {
    this.grad = { ...this.grad, ...g }; this.touch();
    this.u.uGrad.value.set(this.grad.base, this.grad.curve, this.grad.amb);
    return this.grad;
  }



  /** The cross-fade between windows on (default) or off (each message replaces the last at once, as before). */
  setBlend(on: boolean) {
    this.blendOn = on; this.touch();
    if (!on) for (const L of this.levels) { if (!L.blend.done) { L.blend.t0 = -1e12; this.blendStep(L, performance.now(), true); } }
    this.flush();
    return on;
  }

  describe() {
    const lv = this.levels.map(L => L.msg ? {
      level: L.k, t: L.msg.t, ox: L.msg.ox, oy: L.msg.oy, cell: L.msg.vol.dx, size: (L.k === WORLD_LEVEL ? L.msg.world_m : L.msg.vol.n * L.msg.vol.dx), cover: L.msg.cover ?? null,
      ageS: Math.round((performance.now() - L.recvAt) / 100) / 10, gapMs: Math.round(L.gapMs), blendDone: L.blend.done, drift: L.msg.drift,
    } : { level: L.k, loaded: false });
    return { active: this.active, quality: this.quality, scale: this.scale, target: this.tMarch ? `${this.tHist[0].width}x${this.tHist[0].height} (a quarter marched a frame)` : this.rt ? `${this.rt.width}x${this.rt.height}` : 'none (drawn at full size)', temporal: this.temporal, taps: this.u.uTaps.value, erStep: this.u.uStepMax.value, fine: this.u.uFine.value, maxSteps: this.u.uMaxSteps.value, far: this.far, steep: this.steep, grad: this.grad, shade: this.shade, detail: this.detail, layers: this.layers, groundClip: this.groundOn && this.ground ? `on (highest ground ${Math.round(this.u.uGndMax!.value as number)} m)` : 'off',
      lastIngest: this.lastIngest, blend: { on: this.blendOn, stepMs: this.blendMs }, noise: [Math.round(this.noise[0]), Math.round(this.noise[1])], levels: lv };
  }

  dispose() {
    for (const k of Object.keys(this.tex)) this.tex[k as keyof typeof this.tex].dispose();
    (this.u.uDet!.value as THREE.Texture | null)?.dispose(); (this.u.uDetG!.value as THREE.Texture | null)?.dispose();     // the detail noise and its gain table
    this.disposeTarget(this.rt); this.rt = null; this.freeTemporal();
    for (const m of [this.matScreen, this.matSmall, this.matProbe, this.matComp, this.matRes]) m.dispose();
    this.mesh.geometry.dispose();
  }
}
